import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// crypto.randomUUID は HTTPS のみ有効。HTTP+ホスト名アクセス時のフォールバック。
function genUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBreadcrumbStore } from '../app/breadcrumb-store';
import { adminApi, type Project, type DeviceListItem, type AssignedTeam } from '../api/admin';
import { consoleChat, cancelChat, listProjectThreads, listProjectBroadcasts, listAllBroadcasts, getThreadMessages, markThreadRead, archiveThread, unarchiveThread, deleteThread, completeThread, uncompleteThread, cancelThread, uncancelThread, favoriteThread, unfavoriteThread, renameThread, uploadChatFiles, getChatFileBlob, threadStreamUrl, type ThreadSummary, type ThreadAction, type ThreadMessage, type ThreadStreamEvent, type LiveActivityEntry } from '../api/console';
import { useAuthStore } from '../auth/auth-store';
import { PushToggle } from '../components/push-toggle';
import { teamLabel } from '../lib/team-icon';
import { CallButton, CallDock } from '../call/call-dock';
import NotesPanel from './notes-panel';
import { ClaudeUsageWidget } from '../components/claude-usage-widget';
import { useSearchParams } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// チャット（TOP画面）: 左＝プロジェクト。プロジェクトを開いて会話すると、
// チャット欄でそのプロジェクトの中の人（田中くん）が答える。いずれ複数になれば他の人も答える。
// スレッド = claude session_id（会話は永続化され reload で消えない）。

interface Pc {
  deviceId: number;
  deviceName: string;
  online: boolean;
  cwd: string;
  // VS Code Tunnel 名（デバイスに手登録）。null=未登録＝トンネルリンクを出さない。
  tunnelHost: string | null;
  // このDIRに割り当てられた全チーム（1DIR複数チーム対応）。単数割当時は要素1。
  // 死活/デバイス名/送信先の解決は全てこの配列で行う（2チーム目も同じDIRのデバイスに解決される）。
  teamIds: number[];
  projectId: number; // 全プロジェクト・ブロードキャストで、各ターゲットの実PJを引くため
}
interface ProjectChannel {
  projectId: number;
  name: string;
  type: string; // 'dev' | 'company' | 'private'
  people: { id: number; name: string }[];
  pcs: Pc[];
  teams: AssignedTeam[]; // このプロジェクトに DIR でアサインされたチーム（DeviceProjectMap 由来・重複排除）
  archived: boolean; // チャット列でアーカイブ済みか（区切り線の下に出す）
}

function buildProjects(projects: Project[], devices: DeviceListItem[]): ProjectChannel[] {
  return projects
    .map((p) => {
      // このプロジェクトに紐づく DIR(map) を全デバイスから集める。
      const entries = devices.flatMap((w) =>
        w.deviceProjectMap
          .filter((m) => m.projectId === p.id)
          .map((m) => ({ w, m })),
      );
      // 担当チームを重複排除して列挙（teamId 単位）。
      // 1DIR複数チーム: BE が付与する m.teams[](primary+追加割当) を union する。
      // 未対応の古い応答では m.teams が無いので m.team(単数) にフォールバック。
      const teamMap = new Map<number, AssignedTeam>();
      entries.forEach(({ m }) => {
        const list = m.teams ?? (m.teamId != null && m.team ? [m.team] : []);
        list.forEach((t) => teamMap.set(t.id, t));
      });
      return {
        projectId: p.id,
        name: p.nickname || p.name, // 愛称があればチャットでも優先表示
        type: p.type ?? 'dev',
        people: p.people ?? [],
        pcs: entries.map(({ w, m }) => ({
          deviceId: w.id,
          deviceName: w.nickname || w.name,
          online: w.online,
          cwd: m.mountPoint.path,
          tunnelHost: w.tunnelHost,
          // 1DIR複数チーム: このDIRの全チームを持たせる。古い応答(m.teams無し)は単数 m.team にフォールバック。
          teamIds: (m.teams ?? (m.teamId != null && m.team ? [m.team] : [])).map((t) => t.id),
          projectId: p.id,
        })),
        // チーム一覧（設定）でドラッグ＆ドロップして決めた表示順で並べる。同値は id 昇順で安定。
        teams: [...teamMap.values()].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id,
        ),
        archived: p.chatArchived ?? false,
      };
    })
    // チャットに出すのは「DIR割当があり、かつチームが割り当てられている」プロジェクトだけ。
    // チーム未割当のプロジェクトはチャットに表示しない（社長指示 2026-06-11）。
    .filter((c) => c.pcs.length > 0 && c.teams.length > 0);
}

function fmt(iso: string): string {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// メッセージ日時（秒まで）。返信が途中で止まる/緑カーソルが終わらない現象の
// 切り分け用に、送信時刻・返信時刻を秒精度で出す。iso はサーバの createdAt（＝サーバ時刻）。
function fmtSec(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// 鼓動ログの時刻（HH:MM:SS）。progress chunk 受信時刻（client 側の近似。
// 真のサーバ発生時刻が要る場合は backend の chunk に ts を足す＝別対応）。
function hms(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

type Live = { role: 'user' | 'ai' | 'system'; text: string };

// チャット欄に出す画像1枚の表示用データ。
// localUrl=送信直後の楽観表示(ローカル ObjectURL) / fileId=サーバ確定後（認証付きで取得）。
type DispAtt = { key: string; name: string; mime: string; localUrl?: string; fileId?: string };

// コマンド/実行アクション（境界契約 §3.2）の表示用ラベル・色。
const ACTION_STATUS_COLOR: Record<ThreadAction['status'], string> = {
  pending: '#9b9b9b',
  running: '#2563eb',
  ok: '#2bac76',
  error: '#e01e5a',
};
const ACTION_STATUS_LABEL: Record<ThreadAction['status'], string> = {
  pending: '待機',
  running: '実行中',
  ok: '完了',
  error: 'エラー',
};
const actionKindLabel = (kind: ThreadAction['kind']): string => {
  switch (kind) {
    case 'command': return '⌘';
    case 'exec': return '▶';
    case 'skill': return '✨';
    case 'session-control': return '⟳';
    case 'file': return '📄';
    default: return '•';
  }
};

// 「今この依頼が動いているか」を人間に見せる鼓動ラベル。
// Worker が流す progress chunk の raw delta（'thinking' / 'tool:<名前>' / 'tool-result'）を、
// 中身は出さず「何をやっているか」だけの1行に丸める。動いていれば表示がチクチク変わり、
// 止まれば変わらない＝生きているかが一目で分かる（詳細ログの表示が目的ではない）。
function liveActivityLabel(delta: string): string {
  const d = (delta ?? '').trim();
  if (d === 'thinking') return '🤔 考え中…';
  if (d === 'tool-result') return '📄 結果を確認中…';
  if (d.startsWith('tool:')) {
    const tool = d.slice('tool:'.length);
    switch (tool) {
      case 'Bash': return '🔧 コマンド実行中…';
      case 'Edit':
      case 'Write':
      case 'NotebookEdit': return '✏️ ファイル編集中…';
      case 'Read':
      case 'Grep':
      case 'Glob': return '📄 ファイル確認中…';
      case 'WebFetch':
      case 'WebSearch': return '🌐 Web を調べ中…';
      case 'Task': return '🤝 サブ作業を実行中…';
      default: return tool ? `🔧 ${tool} 実行中…` : '🔄 作業中…';
    }
  }
  // 未知の進捗種別でも「動いている」ことは見せる。
  return d ? '🔄 作業中…' : '';
}

// 鼓動ログの開閉コンポーネント。デフォルト最新3件表示・トグルで全件展開。
// 表示フォーマット：前回報告から経過時間ー時刻ー内容
const LIVE_PREVIEW = 3;
function fmtElapsed(cur: { ms?: number }, prev: { ms?: number } | undefined): string {
  if (!prev || !cur.ms || !prev.ms) return '';
  const diff = cur.ms - prev.ms;
  if (diff < 1000) return `+${diff}ms`;
  const s = Math.round(diff / 1000);
  if (s < 60) return `+${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `+${m}m${rem}s` : `+${m}m`;
}
function LiveActivityLog({ entries, streamState }: { entries: import('../api/console').LiveActivityEntry[]; streamState?: string }) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = entries.length > LIVE_PREVIEW;
  const shown = expanded ? entries : entries.slice(-LIVE_PREVIEW);
  const offset = expanded ? 0 : Math.max(0, entries.length - LIVE_PREVIEW);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 7, marginTop: 6, padding: '5px 10px', borderRadius: 12, background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0369a1', fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }} data-testid="live-activity">
      {streamState !== 'final' && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#0ea5e9', flexShrink: 0, marginTop: 5, animation: 'koneccoBlink 1s ease-in-out infinite' }} />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        {hasMore && (
          <button
            onClick={() => setExpanded((v) => !v)}
            style={{ all: 'unset', cursor: 'pointer', color: '#0891b2', fontSize: 11, fontWeight: 700, marginBottom: 2, opacity: 0.8 }}
          >
            {expanded ? '▲ 閉じる' : `▼ 他 ${entries.length - LIVE_PREVIEW} 件`}
          </button>
        )}
        {shown.map((e, ei) => {
          const actualIdx = offset + ei;
          const prev = actualIdx > 0 ? entries[actualIdx - 1] : undefined;
          const elapsed = fmtElapsed(e, prev);
          return (
            <div key={ei} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {elapsed && <span style={{ fontVariantNumeric: 'tabular-nums', color: '#64748b', fontSize: 11, minWidth: 50 }}>{elapsed}</span>}
              {elapsed && <span style={{ opacity: 0.4 }}>ー</span>}
              <span style={{ fontVariantNumeric: 'tabular-nums', color: '#0891b2', opacity: 0.8, fontWeight: 700 }}>{e.t}</span>
              <span style={{ opacity: 0.4 }}>ー</span>
              {e.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// タスク#413: クリップボードへコピーする小さなアイコンボタン。コピー成功後は ✓ を1.2秒表示。
function CopyBtn({ value, title }: { value: string; title: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      type="button"
      style={S.copyBtn}
      title={title}
      onClick={() => {
        void (async () => {
          try {
            await navigator.clipboard.writeText(value);
          } catch {
            // 非セキュアコンテキスト等では clipboard API が使えないので textarea 経由で代替。
            const ta = document.createElement('textarea');
            ta.value = value;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
          }
          setOk(true);
          window.setTimeout(() => setOk(false), 1200);
        })();
      }}
    >{ok ? '✓' : '📋'}</button>
  );
}

// AI-WF251: AI 本文は「表示（整形・既定）／ ソース（生 Markdown）」のタブ切替。
// 既定は整形表示（背景が黒くない方）。ソースは生の markdown をそのまま見たいとき用。
function AiMessageBody({ text, streaming }: { text?: string; streaming: boolean }) {
  const [tab, setTab] = useState<'view' | 'source'>('view');
  return (
    <div style={S.mdBody} className="k-md">
      {text ? (
        <>
          <div style={S.bodyTabs}>
            <button
              type="button"
              style={{ ...S.bodyTab, ...(tab === 'view' ? S.bodyTabActive : null) }}
              onClick={() => setTab('view')}
            >表示</button>
            <button
              type="button"
              style={{ ...S.bodyTab, ...(tab === 'source' ? S.bodyTabActive : null) }}
              onClick={() => setTab('source')}
            >ソース</button>
            {/* タスク#413: 本文（生 Markdown）をそのままコピー。 */}
            <CopyBtn value={text} title="メッセージをコピー" />
          </div>
          {tab === 'view' ? (
            <Markdown remarkPlugins={[remarkGfm]}>{text}</Markdown>
          ) : (
            <pre style={S.bodySource}>{text}</pre>
          )}
        </>
      ) : null}
      {streaming && (
        text ? (
          <span style={S.streamCaret}>▌</span>
        ) : (
          <span style={S.typingInline}>
            <span style={S.spinner} /> 考え中…
          </span>
        )
      )}
    </div>
  );
}

// カラム幅の永続化（localStorage）。値が無い／壊れている場合はデフォルトを使う。
const LS_SIDE_W = 'konecco.console.sideWidth';
const LS_TEAM_W = 'konecco.console.teamWidth';
const LS_THREADS_W = 'konecco.console.threadsWidth';
// チャット表示モード（line=LINE風バブル / slack=Slack風）。localStorage 保存。
const LS_CHAT_MODE = 'konecco.console.chatMode';
// AI-WF262 (要望#211/TA-5): 非同期モード（📮 留守番）の ON/OFF。localStorage 保存。
const LS_ASYNC_MODE = 'konecco.console.asyncMode';
const DEFAULT_SIDE_W = 230;
const DEFAULT_TEAM_W = 200;
const DEFAULT_THREADS_W = 232;
const SIDE_MIN = 160;
const SIDE_MAX = 420;
const TEAM_MIN = 140;
const TEAM_MAX = 360;
const THREADS_MIN = 160;
const THREADS_MAX = 480;

// AI-WF329(#259): iPad 横などタッチ端末の横向きでは、3カラムの初期合計(230+200+232=662px)が
// 広すぎてチャット本体(main)が窮屈になる(1024px で約330px)。そこで localStorage 未設定時のみ、
// 「横向き＋粗ポインタ(タッチ)＋タブレット幅」を検出したら初期列幅を各 MIN 相当へ寄せて
// 開いた瞬間から main を実用幅(≈530px@1024)にする。DEFAULT 定数と PC(細ポインタ)の初期値は不変、
// 以後はユーザーのドラッグ結果(localStorage)を常に尊重する。
function isTabletLandscape(): boolean {
  try {
    return (
      window.matchMedia('(pointer: coarse)').matches &&
      window.innerWidth > window.innerHeight &&
      window.innerWidth <= 1366
    );
  } catch {
    return false;
  }
}
// 初期幅: 保存値があれば最優先。無いときだけ端末で分岐(タブレット横=tabletDef / それ以外=def)。
function initialWidth(key: string, def: number, tabletDef: number): number {
  try {
    const v = localStorage.getItem(key);
    if (v != null) {
      const n = parseInt(v, 10);
      if (Number.isFinite(n)) return n;
    }
  } catch {
    return def;
  }
  return isTabletLandscape() ? tabletDef : def;
}

// 入力欄(textarea)の高さを localStorage に記憶（社長指示: 高さ可変＋記憶）。
const LS_TA_H = 'konecco.console.composerHeight';
function loadTaHeight(): number | null {
  try {
    const v = localStorage.getItem(LS_TA_H);
    if (v == null) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

// スレッドごとのスクロール位置を localStorage に記憶（会話キー=conversationId → scrollTop）。
// 未読があるときは「最初の未読」へジャンプし、無ければ前回位置を復元する（社長指示）。
const LS_SCROLL = 'konecco.console.scrollPos';
function loadScrollMap(): Record<string, number> {
  try {
    const o = JSON.parse(localStorage.getItem(LS_SCROLL) || '{}');
    return o && typeof o === 'object' ? o : {};
  } catch {
    return {};
  }
}
function saveScrollPos(conv: string, top: number): void {
  try {
    const m = loadScrollMap();
    m[conv] = Math.round(top);
    // 際限なく溜まらないよう、古いキーから間引く（FIFO）。
    const keys = Object.keys(m);
    if (keys.length > 200) delete m[keys[0]];
    localStorage.setItem(LS_SCROLL, JSON.stringify(m));
  } catch {
    /* ignore */
  }
}

// 有効チームに属さない（チーム削除済み/未割当）会話をまとめる仮想チーム。
const UNCLASSIFIED = -1;
// ブロードキャスト仮想チーム（DBには作らない・UI固定項目）。選ぶと「PJ内の全チームへ一斉発信」モード。
// 返信は各チームの実体スレッドで完結し、ここは束ね表示＋新規一斉送信の窓口に徹する。
const BROADCAST = -2;
// お気に入り仮想チーム（DBには作らない・UI固定項目）。チームをまたいでお気に入り登録したスレッドの一覧。
const FAVORITES = -3;
// 全プロジェクト・ブロードキャスト仮想プロジェクト（左端の一番上・DB非依存）。
// 選ぶと「全PJ × 全チームへ一斉発信」モード。activeTeamId は BROADCAST に固定して既存UIを流用。
const GLOBAL_BC_ID = -100;
// 全体お気に入り仮想プロジェクト（左端・DB非依存）。選ぶと全PJ横断のお気に入り一覧。
// activeTeamId は FAVORITES に固定して既存UIを流用。開くと該当PJ/チーム/スレッドへ移動する。
const GLOBAL_FAV_ID = -101;

// 全体お気に入りの1行。横断表示にPJ名/チーム名が要るので ThreadSummary に付けて持つ。
// jumpTeamId は移動先チーム（所属チームが削除済みなら UNCLASSIFIED に解決済み）。
type GlobalFavThread = ThreadSummary & {
  projectId: number;
  projectName: string;
  teamName: string;
  jumpTeamId: number;
};

// /コマンドの種別(kind)アイコン（チャットの / 一覧の見出し用）。
const COMMAND_KIND_ICON: Record<string, string> = {
  builtin: '⌨️',
  skill: '🧩',
  custom: '⚡',
  intent: '💡',
  exec: '🖥️',
};

// 選択状態の永続化（社長指示 2026-06-11）。チャットの木「プロジェクト→チーム→スレッド」の
// 各分岐で最後に選んだ子を覚える＝トーナメント型。例: P1/開発1部/スレッド1 を見て 開発2部 に行き
// 開発1部 に戻ると スレッド1 が選択された状態に戻る。
const LS_NAV = 'konecco.console.nav';

type NavState = {
  lastProject: number | null;
  // projectId -> { lastTeam, teamId -> 最後に選んだ会話(null=新しいスレッド) }
  projects: Record<string, { lastTeam: number | null; threads: Record<string, string | null> }>;
};

function loadNav(): NavState {
  try {
    const v = localStorage.getItem(LS_NAV);
    if (v) {
      const o = JSON.parse(v);
      if (o && typeof o === 'object') {
        return {
          lastProject: typeof o.lastProject === 'number' ? o.lastProject : null,
          projects: o.projects && typeof o.projects === 'object' ? o.projects : {},
        };
      }
    }
  } catch { /* ignore */ }
  return { lastProject: null, projects: {} };
}
function saveNav(nav: NavState): void {
  try { localStorage.setItem(LS_NAV, JSON.stringify(nav)); } catch { /* ignore */ }
}
function navProject(nav: NavState, pid: number) {
  let p = nav.projects[pid];
  if (!p) { p = { lastTeam: null, threads: {} }; nav.projects[pid] = p; }
  return p;
}
function getLastProject(): number | null { return loadNav().lastProject; }
function setLastProject(pid: number): void { const n = loadNav(); n.lastProject = pid; saveNav(n); }
function getLastTeam(pid: number): number | null { return loadNav().projects[pid]?.lastTeam ?? null; }
function setLastTeam(pid: number, teamId: number | null): void {
  const n = loadNav(); navProject(n, pid).lastTeam = teamId; saveNav(n);
}
// その (プロジェクト×チーム) で最後に選んだ会話。記録が無ければ null（=新しいスレッド）。
function getLastThread(pid: number, teamId: number | null): string | null {
  return loadNav().projects[pid]?.threads[String(teamId)] ?? null;
}
function setLastThread(pid: number, teamId: number | null, conversationId: string | null): void {
  const n = loadNav(); navProject(n, pid).threads[String(teamId)] = conversationId; saveNav(n);
}

// カラム境界のドラッグハンドル。掴みやすいよう当たり判定を広く取り、
// ホバー／ドラッグ中は中央の線をハイライトして「掴める」ことを示す。
// AI-WF329(#259): マウス専用イベント(onMouse*)を Pointer Events 化し、iPad の指/
// Apple Pencil でも境界をドラッグできるようにする。Pointer Events はマウスも包含するため
// PC の挙動(ホバー/ドラッグのハイライト)は不変（単一経路で回帰しない）。
function Resizer({ onStart }: { onStart: (e: React.PointerEvent) => void }) {
  const [hot, setHot] = useState(false);
  const [drag, setDrag] = useState(false);
  const active = hot || drag;
  return (
    <div
      style={S.resizer}
      onPointerEnter={() => setHot(true)}
      onPointerLeave={() => setHot(false)}
      onPointerDown={(e) => {
        setDrag(true);
        const onUp = () => {
          setDrag(false);
          window.removeEventListener('pointerup', onUp);
          window.removeEventListener('pointercancel', onUp);
        };
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        onStart(e);
      }}
      title="ドラッグで幅を調整"
    >
      <div style={{ ...S.resizerLine, ...(active ? S.resizerLineActive : null) }} />
    </div>
  );
}

// 未読バッジ。n<=0 は非表示。99超は 99+。
function Badge({ n, style }: { n: number; style?: React.CSSProperties }) {
  if (n <= 0) return null;
  return <span style={{ ...badgeStyle, ...style }}>{n > 99 ? '99+' : n}</span>;
}
const badgeStyle: React.CSSProperties = {
  flexShrink: 0,
  minWidth: 18,
  height: 18,
  padding: '0 5px',
  borderRadius: 999,
  background: '#f43f5e',
  color: '#fff',
  fontSize: 11,
  fontWeight: 700,
  lineHeight: '18px',
  textAlign: 'center',
  display: 'inline-block',
};

// チャット欄の画像1枚。サーバ画像は <img> に Bearer を載せられないため、
// 認証付き API で blob を取得して ObjectURL 化して表示する。クリックで拡大ダイアログ。
function ChatImage({ att, onOpen }: { att: DispAtt; onOpen: (url: string, name: string) => void }) {
  const [url, setUrl] = useState<string | null>(att.localUrl ?? null);
  useEffect(() => {
    if (att.localUrl) { setUrl(att.localUrl); return; }
    if (!att.fileId) return;
    let revoked = false;
    let obj: string | null = null;
    getChatFileBlob(att.fileId)
      .then((blob) => {
        if (revoked) return;
        obj = URL.createObjectURL(blob);
        setUrl(obj);
      })
      .catch(() => { /* 取得失敗時はプレースホルダのまま */ });
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [att.fileId, att.localUrl]);
  if (!url) return <div style={S.imgPlaceholder} title={att.name} />;
  return (
    <img
      src={url}
      alt={att.name}
      title={att.name}
      style={S.chatImg}
      onClick={() => onOpen(url, att.name)}
    />
  );
}

function useIsMobile() {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const h = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  return mobile;
}

export function ConsolePage() {
  const qc = useQueryClient();
  const { data: devices = [] } = useQuery({
    queryKey: ['devices'],
    queryFn: adminApi.listDevices,
    refetchInterval: 15_000,
  });
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: adminApi.listProjects });
  const list = useMemo(() => buildProjects(projects, devices), [projects, devices]);

  const [activeId, setActiveId] = useState<number | null>(() => getLastProject());
  // ヘッダー全件検索からの遷移（/?open=<conv>&pj=<projectId>&team=<teamId>）を受ける。
  const [searchParams, setSearchParams] = useSearchParams();
  const globalBC = activeId === GLOBAL_BC_ID;
  const globalFav = activeId === GLOBAL_FAV_ID;
  // 全プロジェクト・ブロードキャスト用の合成チャンネル。全PJの teams/pcs を集約して持つ
  // （pc は実PJの projectId を保持済み）。本物の active と同じ形なので既存UIをそのまま流用できる。
  const globalChannel = useMemo<ProjectChannel | null>(() => {
    if (list.length === 0) return null;
    const teamMap = new Map<number, AssignedTeam>();
    for (const c of list) for (const t of c.teams) teamMap.set(t.id, t);
    return {
      projectId: GLOBAL_BC_ID,
      name: '全プロジェクト',
      type: 'dev',
      people: [],
      pcs: list.flatMap((c) => c.pcs),
      // ここも表示順（チーム一覧の D&D）で並べる。
      teams: [...teamMap.values()].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id,
      ),
      archived: false,
    };
  }, [list]);
  // 全体お気に入り用の合成チャンネル。中身は globalChannel と同じで projectId だけ変える
  // （プロジェクト切替 effect が projectId 変化で走るため、全PJ BC と区別できる id が要る）。
  const globalFavChannel = useMemo<ProjectChannel | null>(
    () => (globalChannel ? { ...globalChannel, projectId: GLOBAL_FAV_ID, name: 'お気に入り' } : null),
    [globalChannel],
  );
  const active = globalBC
    ? globalChannel
    : globalFav
      ? globalFavChannel
      : list.find((c) => c.projectId === activeId) ?? list[0] ?? null;
  const personName = active?.people[0]?.name ?? '担当AI';

  // 既読化はサーバ管理。スレッドを既読にしたら threads を再取得して未読数を更新する。
  const markReadMut = useMutation({
    mutationFn: (conversationId: string) => markThreadRead(conversationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['threads'] }),
  });
  // スレッドのアーカイブ / 復活（物理削除しない）。完了後にスレッド一覧を再取得。
  const archiveMut = useMutation({
    mutationFn: ({ conversationId, archived }: { conversationId: string; archived: boolean }) =>
      archived ? archiveThread(conversationId) : unarchiveThread(conversationId),
    // 開いているスレッドをアーカイブ（閉じる操作）したら選択解除し新規スレッドの空状態へ（AI-WF330）。
    onSuccess: (_d, { conversationId, archived }) => {
      if (archived && activeSession === conversationId) setActiveSession(null);
      qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
  // スレッドの完全削除（物理削除）。アーカイブ欄からのみ。開いていたスレなら選択解除。
  const deleteMut = useMutation({
    mutationFn: (conversationId: string) => deleteThread(conversationId),
    onSuccess: (_d, conversationId) => {
      if (activeSession === conversationId) setActiveSession(null);
      qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
  // スレッドタイトルのインライン編集状態（ダブルクリックで起動）。
  const [editingTitle, setEditingTitle] = useState<{ conversationId: string; value: string } | null>(null);
  const renameMut = useMutation({
    mutationFn: ({ conversationId, title }: { conversationId: string; title: string }) =>
      renameThread(conversationId, title),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['threads'] }),
  });
  const commitTitleEdit = () => {
    if (!editingTitle) return;
    renameMut.mutate({ conversationId: editingTitle.conversationId, title: editingTitle.value });
    setEditingTitle(null);
  };

  // スレッドの完了・中止 / 復活。
  const completeMut = useMutation({
    mutationFn: ({ conversationId, completed }: { conversationId: string; completed: boolean }) =>
      completed ? completeThread(conversationId) : uncompleteThread(conversationId),
    // 開いているスレッドを完了（閉じる操作）したら選択解除し新規スレッドの空状態へ（AI-WF330）。
    onSuccess: (_d, { conversationId, completed }) => {
      if (completed && activeSession === conversationId) setActiveSession(null);
      qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
  const cancelMut = useMutation({
    mutationFn: ({ conversationId, cancelled }: { conversationId: string; cancelled: boolean }) =>
      cancelled ? cancelThread(conversationId) : uncancelThread(conversationId),
    // 開いているスレッドを中止（閉じる操作）したら選択解除し新規スレッドの空状態へ（AI-WF330）。
    onSuccess: (_d, { conversationId, cancelled }) => {
      if (cancelled && activeSession === conversationId) setActiveSession(null);
      qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
  const favoriteMut = useMutation({
    mutationFn: ({ conversationId, favorited }: { conversationId: string; favorited: boolean }) =>
      favorited ? favoriteThread(conversationId) : unfavoriteThread(conversationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['threads'] }),
  });
  // アーカイブ欄の開閉（スレッド）。
  const [showArchived, setShowArchived] = useState(false);
  // 完了・中止セクションの開閉。
  const [showCompleted, setShowCompleted] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  // サイドバー左列の折りたたみ：プロジェクト一覧 / プロジェクトのアーカイブ区画。
  const [showProjects, setShowProjects] = useState(true);
  const [showProjectArchive, setShowProjectArchive] = useState(false);
  const [collapsedTypes, setCollapsedTypes] = useState<Set<string>>(new Set());

  // 選択中のチーム（②列）。プロジェクト切替でリセット。null=未選択（全体）。
  const [activeTeamId, setActiveTeamId] = useState<number | null>(null);
  // ②列でチームの下に置く「メモ / タスク」を選ぶと、③スレッド列＋④会話欄を
  // NotesPanel（一覧＋編集）に差し替える。チームを選ぶと 'threads' へ戻す。
  const [activeView, setActiveView] = useState<'threads' | 'memos' | 'tasks'>('threads');

  // @メンション候補のためにチーム詳細（メンバー/スロット）とコントローラ担当者を引く。
  const { data: teams = [] } = useQuery({ queryKey: ['teams'], queryFn: adminApi.listTeams });
  const { data: controller } = useQuery({
    queryKey: ['controller-persona'],
    queryFn: adminApi.getControllerPersona,
  });
  const renameController = useMutation({
    mutationFn: adminApi.updateControllerPersona,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['controller-persona'] }),
  });
  const activeTeam = teams.find((t) => t.id === activeTeamId) ?? null;
  // チャットの / コマンド一覧（AI-WF #91/#96）。global＋選択中 project/team を合成した有効一覧を引く。
  // backend が同名を team>project>global で override 済み。team は実在 id のときだけ渡す（UNCLASSIFIED は除外）。
  // 全プロジェクト・ブロードキャスト/全体お気に入りの仮想PJ(-100/-101)は実在しないので project スコープは渡さない。
  const activeProjectId = globalBC || globalFav ? undefined : active?.projectId;
  const realTeamId =
    activeTeamId != null && activeTeamId !== UNCLASSIFIED && activeTeamId !== BROADCAST && activeTeamId !== FAVORITES
      ? activeTeamId
      : undefined;
  const { data: availCommands = [] } = useQuery({
    queryKey: ['available-commands', activeProjectId, realTeamId],
    queryFn: () => adminApi.availableCommands(activeProjectId, realTeamId),
  });
  // 既定の応答者＝チームのPM(リーダー枠の担当)。@無しのときはこの人。
  const pmAgent = activeTeam?.slots?.find((s) => s.isLeader)?.agent ?? null;
  const pmName = pmAgent?.nickname || pmAgent?.name || null;
  // パネルヘッダに出す、選択中チームのメンバー一覧（PM=リーダーを先頭・👑）。
  const teamMembers = (activeTeam?.slots ?? [])
    .filter((s) => s.agent)
    .map((s) => ({
      id: s.agent!.id,
      name: s.agent!.nickname || s.agent!.name,
      icon: s.agent!.icon,
      isLeader: s.isLeader,
    }))
    .sort((a, b) => Number(b.isLeader) - Number(a.isLeader));
  // @候補：選択中チームの割当メンバー（役割順・PMに👑）＋コントローラ担当者（全チャット常在）。
  const mentionCandidates = useMemo(() => {
    const members = (activeTeam?.slots ?? [])
      .filter((s) => s.agent)
      .map((s) => ({
        ref: `agent:${s.agent!.id}`,
        label: s.agent!.nickname || s.agent!.name,
        icon: s.agent!.icon,
        sub: s.isLeader ? '👑 PM' : '',
      }));
    const ctrl = controller
      ? [{ ref: 'controller', label: controller.name, icon: controller.icon, sub: '⚙ コントローラ' }]
      : [];
    return [...members, ...ctrl];
  }, [activeTeam, controller]);

  // fromRef(agent:<id> / controller) → 表示名・アイコンの解決。全チームのスロットから引く。
  const agentById = useMemo(() => {
    const m = new Map<number, { name: string; icon?: string }>();
    for (const t of teams) {
      for (const s of t.slots ?? []) {
        if (s.agent) m.set(s.agent.id, { name: s.agent.nickname || s.agent.name, icon: s.agent.icon ?? undefined });
      }
    }
    return m;
  }, [teams]);
  // メッセージの発言者を解決：AIは fromRef（解決済み話者）→ エージェント/コントローラ、無ければPM/担当AI。
  const resolveSender = (role: string, fromRef?: string | null): { name: string; icon?: string } => {
    if (role === 'user') return { name: 'あなた' };
    if (role === 'system') return { name: 'システム' };
    if (fromRef === 'controller' && controller) return { name: controller.name, icon: controller.icon ?? undefined };
    if (fromRef && fromRef.startsWith('agent:')) {
      const a = agentById.get(Number(fromRef.slice('agent:'.length)));
      if (a) return a;
    }
    // fromRef 無し（旧データ等）はチームPMにフォールバック（アイコンも出す）。
    return { name: pmName || personName, icon: pmAgent?.icon ?? undefined };
  };

  // @/オートコンプリート。textarea のキャレット直前トークンを見て候補を出す。
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  // mention: 今編集中の @ または / トークン（kind/クエリ/開始位置）。null=非表示。
  const [mention, setMention] = useState<{ kind: '@' | '/'; query: string; start: number } | null>(null);
  // 送信時に渡す @宛先（候補選択で確定）。未確定なら undefined＝チームPM既定。
  const [mentionRef, setMentionRef] = useState<string | null>(null);
  // 候補ドロップダウンのハイライト位置（↑↓で移動・Enter/Tabで確定）。
  const [mentionIndex, setMentionIndex] = useState(0);

  // textarea のコールバック ref。マウント時に記憶した高さを復元し、
  // ユーザーがドラッグでリサイズしたら ResizeObserver で高さを localStorage に保存する。
  // （React の inline style で高さを制御すると native resize と競合するため ref で直接操作する。）
  const taResizeCleanup = useRef<(() => void) | null>(null);
  const setTaEl = (el: HTMLTextAreaElement | null) => {
    taRef.current = el;
    if (taResizeCleanup.current) { taResizeCleanup.current(); taResizeCleanup.current = null; }
    if (el) {
      const stored = loadTaHeight();
      // 初期は高め(120px)。前回ドラッグで変えた高さがあればそれを優先して復元する。
      el.style.height = stored ? `${stored}px` : '120px';
      const ro = new ResizeObserver(() => {
        const h = el.offsetHeight;
        if (h > 0) { try { localStorage.setItem(LS_TA_H, String(h)); } catch { /* ignore */ } }
      });
      ro.observe(el);
      taResizeCleanup.current = () => ro.disconnect();
    }
  };

  // 入力欄の「上端バー」をつかんで上へドラッグ＝高くする（下へ＝低くする）。
  // 画面下にある入力欄では下角より上端を掴む方が自然なため、専用ハンドルで実装。
  const startComposerResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const ta = taRef.current;
    if (!ta) return;
    const startY = e.clientY;
    const startH = ta.offsetHeight;
    const min = 80;
    const max = Math.round(window.innerHeight * 0.7);
    const onMove = (ev: MouseEvent) => {
      // 上へ動かす(ev.clientY が小さくなる)と差分が正＝高くなる。
      const next = Math.min(max, Math.max(min, startH + (startY - ev.clientY)));
      ta.style.height = `${next}px`;
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // ResizeObserver でも保存されるが、最終値を確実に保存しておく。
      try { localStorage.setItem(LS_TA_H, String(taRef.current?.offsetHeight ?? startH)); } catch { /* ignore */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  };

  // チャットログのスクロール制御（位置記憶＋未読ジャンプ）。
  const logRef = useRef<HTMLDivElement | null>(null);
  const unreadDividerRef = useRef<HTMLDivElement | null>(null);
  // 「ここから未読」区切りを出すサーバメッセージのインデックス（開いた時点で確定・スレッド切替まで保持）。
  const [unreadAnchorIdx, setUnreadAnchorIdx] = useState<number | null>(null);
  // 鼓動ログの永続保持（メッセージ数値id→エントリ）。TanStack Query キャッシュは
  // 別スレッドから戻ると refetch で server データに置換され client 専用の liveActivity が
  // 消える。ここに退避しておき、shown 構築時にマージして再取得後も鼓動ログを残す。
  const liveActivityRef = useRef<Map<number, LiveActivityEntry[]>>(new Map());
  const unreadCountRef = useRef(0); // 開いた瞬間の未読件数（既読化される前に控える）
  const pendingPosRef = useRef<string | null>(null); // メッセージ読込後に位置決めする会話
  const scrollLockRef = useRef(false); // 位置復元中はスクロール保存を止める
  const needsUnreadScrollRef = useRef(false); // 未読ジャンプが必要か（divider 描画後に useLayoutEffect で実行）
  const stickBottomRef = useRef(0); // 直近の表示メッセージ件数（新着追従の判定用）
  const atBottomRef = useRef(true); // 新着が来る「前」に下端付近にいたか（onScrollで更新）
  const [, setShowJump] = useState(false); // ↓ ボタンは常時表示のため読み取り不要・setShowJump は新着バッジ管理に残す
  const [jumpCount, setJumpCount] = useState(0);   // 上スクロール中に届いた新着件数

  // プロジェクト列の DnD（並べ替え＋アーカイブ）。dragId=ドラッグ中のプロジェクト。
  const [dragId, setDragId] = useState<number | null>(null);
  const arrange = useMutation({
    mutationFn: adminApi.setChatArrangement,
    onMutate: async (items) => {
      await qc.cancelQueries({ queryKey: ['projects'] });
      const prev = qc.getQueryData<Project[]>(['projects']);
      // 楽観更新：キャッシュ上のプロジェクトに新しい順序・アーカイブ状態を反映。
      qc.setQueryData<Project[]>(['projects'], (old) => {
        if (!old) return old;
        const byId = new Map(old.map((p) => [p.id, p]));
        const reordered = items
          .map((it, idx) => {
            const p = byId.get(it.id);
            return p ? { ...p, chatOrder: idx, chatArchived: it.archived } : null;
          })
          .filter((p): p is NonNullable<typeof p> => p != null);
        // items に無い（PC未割当などで非表示の）プロジェクトは末尾にそのまま残す。
        const rest = old.filter((p) => !items.some((it) => it.id === p.id));
        return [...reordered, ...rest];
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['projects'], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });

  // dragId を targetId の手前（null=末尾）へ、archived 区画かどうかも指定して移動した配置を作る。
  const arrangementAfterMove = (
    draggedId: number,
    targetId: number | null,
    intoArchived: boolean,
  ): { id: number; archived: boolean }[] => {
    const activeIds = list.filter((c) => !c.archived).map((c) => c.projectId);
    const archivedIds = list.filter((c) => c.archived).map((c) => c.projectId);
    const a = activeIds.filter((id) => id !== draggedId);
    const ar = archivedIds.filter((id) => id !== draggedId);
    const dest = intoArchived ? ar : a;
    const at = targetId == null ? dest.length : Math.max(0, dest.indexOf(targetId));
    dest.splice(at, 0, draggedId);
    return [
      ...a.map((id) => ({ id, archived: false })),
      ...ar.map((id) => ({ id, archived: true })),
    ];
  };
  const onDropOn = (targetId: number | null, intoArchived: boolean) => {
    if (dragId == null) return;
    arrange.mutate(arrangementAfterMove(dragId, targetId, intoArchived));
    setDragId(null);
  };

  // ADR0008: スレッドキー = conversationId（activeSession に保持）。
  const [activeSession, setActiveSession] = useState<string | null>(null);

  // チャットは会話(スレッド)ごとに独立・並列。下書き/送信中/楽観表示はすべて会話キーで分離し、
  // スレッドを切り替えても別スレッドの内容が残らないようにする（社長指示 2026-06-11）。
  type LiveItem = Live & { id: number; atts?: DispAtt[] };
  const [draftByKey, setDraftByKey] = useState<Record<string, string>>({});
  const [liveByConv, setLiveByConv] = useState<Record<string, LiveItem[]>>({});
  const [pendingByConv, setPendingByConv] = useState<Record<string, number>>({});
  // AI-WF362: busy=true の間に送信した C の内容。C のキュー起動時にクリア。
  const [waitBubbleByConv, setWaitBubbleByConv] = useState<Record<string, string | null>>({});
  const queuesRef = useRef<Map<string, Promise<void>>>(new Map()); // 会話ごとの送信キュー（順次）
  const resumeRef = useRef<Map<string, string>>(new Map()); // 会話ごとの CLI 継続トークン
  const liveIdRef = useRef(0);
  // AI-WF338 #268: streaming chunk バッチバッファ。per-token re-render を rAF 単位に束ねる。
  const chunkBufRef = useRef<Map<string | number, { delta: string; seq: number }>>(new Map());
  const flushRafRef = useRef<number | null>(null);
  // streaming テキスト全量の蓄積。rAF フラッシュは cur.text+delta（差分）でなくこの全量で上書きする。
  // 理由: consoleChat レスポンス後の invalidateQueries→refetch でキャッシュが DB の古い状態に巻き戻り、
  // その後の rAF が「バッファ内の直近 delta のみ」を古いベースに足してしまい欠落が生じる（"像を確認します。画" バグ）。
  // 全量を持てば refetch 後でも次の rAF flush で正しいテキストに戻せる。
  const streamAccumRef = useRef<Map<string | number, { text: string; lastSeq: number }>>(new Map());
  // 下書きキー：既存スレッドは conversationId、新規スレッドは (project×team) ごとに分離。
  const composerKey = active ? activeSession ?? `new:${active.projectId}:${activeTeamId}` : '';
  const input = draftByKey[composerKey] ?? '';
  const setInput = (v: string) => setDraftByKey((d) => ({ ...d, [composerKey]: v }));
  const live: LiveItem[] = activeSession ? liveByConv[activeSession] ?? [] : [];

  // 添付画像（送信前のプレビュー）。会話キーで分離（下書きと同じ扱い）。
  type PendingAtt = { id: number; file: File; url: string; name: string; mime: string };
  const [attachByKey, setAttachByKey] = useState<Record<string, PendingAtt[]>>({});
  const attachIdRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // 画像拡大ダイアログ（プレビューの✗クリック／チャット内画像クリックで開く）。
  const [dialog, setDialog] = useState<{ url: string; name: string } | null>(null);
  const pendingAtts = attachByKey[composerKey] ?? [];

  // 画像ファイルを送信前プレビューに追加（ファイル選択・貼り付け・スクショ共通）。
  const addFiles = (files: FileList | File[]) => {
    const imgs = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (!imgs.length) return;
    const items: PendingAtt[] = imgs.map((f) => ({
      id: ++attachIdRef.current,
      file: f,
      url: URL.createObjectURL(f),
      name: f.name || 'image.png',
      mime: f.type,
    }));
    setAttachByKey((m) => ({ ...m, [composerKey]: [...(m[composerKey] ?? []), ...items] }));
  };
  const removeAtt = (id: number) => {
    setAttachByKey((m) => {
      const list = m[composerKey] ?? [];
      const target = list.find((x) => x.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return { ...m, [composerKey]: list.filter((x) => x.id !== id) };
    });
  };
  // クリップボードからの画像貼り付け（スクショ Ctrl+V を含む）。
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData.items)
      .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
      .map((it) => it.getAsFile())
      .filter((f): f is File => !!f);
    if (files.length) { e.preventDefault(); addFiles(files); }
  };
  const busy = activeSession ? (pendingByConv[activeSession] ?? 0) > 0 : false;
  const appendLive = (conv: string, item: { role: Live['role']; text: string }) =>
    setLiveByConv((m) => ({ ...m, [conv]: [...(m[conv] ?? []), { id: ++liveIdRef.current, ...item }] }));
  const bumpPending = (conv: string, d: number) =>
    setPendingByConv((m) => ({ ...m, [conv]: Math.max(0, (m[conv] ?? 0) + d) }));

  // 左(プロジェクト) / 中央(スレッド) の幅。localStorage から復元、無ければデフォルト。
  // AI-WF329(#259): 初期幅は保存値優先。未保存かつタブレット横のときだけ各 MIN 相当に寄せる(A')。
  const [sideWidth, setSideWidth] = useState(() => initialWidth(LS_SIDE_W, DEFAULT_SIDE_W, SIDE_MIN));
  const [teamWidth, setTeamWidth] = useState(() => initialWidth(LS_TEAM_W, DEFAULT_TEAM_W, TEAM_MIN));
  const [threadsWidth, setThreadsWidth] = useState(() => initialWidth(LS_THREADS_W, DEFAULT_THREADS_W, THREADS_MIN));
  useEffect(() => {
    try { localStorage.setItem(LS_SIDE_W, String(sideWidth)); } catch { /* ignore */ }
  }, [sideWidth]);
  useEffect(() => {
    try { localStorage.setItem(LS_TEAM_W, String(teamWidth)); } catch { /* ignore */ }
  }, [teamWidth]);
  useEffect(() => {
    try { localStorage.setItem(LS_THREADS_W, String(threadsWidth)); } catch { /* ignore */ }
  }, [threadsWidth]);

  const isMobile = useIsMobile();
  // スマホ時のドリルダウンレベル: 1=プロジェクト / 2=チーム / 3=スレッド / 4=会話。
  const [mobileLevel, setMobileLevel] = useState(1);

  // チャット表示モード（LINE風 / Slack風）。localStorage 保存・既定は line。
  const [chatMode, setChatMode] = useState<'line' | 'slack'>(() => {
    try { return localStorage.getItem(LS_CHAT_MODE) === 'slack' ? 'slack' : 'line'; } catch { return 'line'; }
  });
  useEffect(() => {
    try { localStorage.setItem(LS_CHAT_MODE, chatMode); } catch { /* ignore */ }
  }, [chatMode]);

  // AI-WF262 (要望#211/TA-5): 非同期モード（📮）。ON で consoleChat に async:true を付け、
  // 「受け取りました」→背面→完了push→タップで結果、の留守番体験になる。既定 OFF=従来同期。
  const [asyncMode, setAsyncMode] = useState<boolean>(() => {
    try { return localStorage.getItem(LS_ASYNC_MODE) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(LS_ASYNC_MODE, asyncMode ? '1' : '0'); } catch { /* ignore */ }
  }, [asyncMode]);

  // 境界ドラッグでカラム幅を変更。pointerdown 中だけ window にリスナを張る。
  // AI-WF329(#259): マウス専用(MouseEvent/mousemove/mouseup)から Pointer Events 化。
  // 指/ペンでもドラッグ可。setPointerCapture で細いハンドルからポインタが外れても継続、
  // pointercancel でも確実に解放し、pointerId ガードで複数ポインタの混線を防ぐ。
  // clientX 起点の差分計算は不変なので PC マウスのドラッグ挙動は従来通り。
  const startResize = (
    e: React.PointerEvent,
    current: number,
    min: number,
    max: number,
    setter: (n: number) => void,
  ) => {
    e.preventDefault();
    const pointerId = e.pointerId;
    const startX = e.clientX;
    const startW = current;
    try { (e.currentTarget as Element).setPointerCapture?.(pointerId); } catch { /* ignore */ }
    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      const next = Math.min(max, Math.max(min, startW + (ev.clientX - startX)));
      setter(next);
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // 全プロジェクトのスレッドを取得（未読バッジの集計にプロジェクト横断データが要るため）。
  // 表示時のチーム絞り込みはクライアント側で行う。
  const threadQueries = useQueries({
    queries: list.map((p) => ({
      queryKey: ['threads', p.projectId],
      queryFn: () => listProjectThreads(p.projectId),
      enabled: list.length > 0,
      // AI-WF251: 裏に回した会話の未読反映を早める（20s→8s）。裏スレの返信は
      // アクティブ会話専用のSSE対象外で、このポーリングでしか未読が出ないため、
      // 間隔が長いと「裏スレの未読が出ない→次tickでまとめて出る」ように見える。
      refetchInterval: 8_000,
    })),
  });
  const threadsByProject = useMemo(() => {
    const m = new Map<number, ThreadSummary[]>();
    list.forEach((p, i) => m.set(p.projectId, threadQueries[i]?.data ?? []));
    return m;
    // threadQueries は毎レンダ新規配列だが data 参照は安定。簡潔さ優先で依存に含める。
  }, [list, threadQueries]);
  const projThreads = useMemo(
    () => (active ? threadsByProject.get(active.projectId) ?? [] : []),
    [active, threadsByProject],
  );
  // スレッドの所属チーム。有効チームに無い(削除済み/未割当)会話は「未分類」バケットへ。
  const teamIdSet = useMemo(
    () => new Set((active?.teams ?? []).map((t) => t.id)),
    [active],
  );
  const effTeam = (t: ThreadSummary): number =>
    t.teamId != null && teamIdSet.has(t.teamId) ? t.teamId : UNCLASSIFIED;
  // 未分類スレッドが1つでもあれば、チーム列に「未分類」項目を出す。
  const hasUnclassified = projThreads.some((t) => effTeam(t) === UNCLASSIFIED);
  // チーム絞り込み後の表示用スレッド（選択バケットに属するものだけ）。
  // アーカイブ・完了・中止済みは通常一覧から外し、それぞれのセクションに分ける。
  const bucketThreads = projThreads.filter((t) => effTeam(t) === activeTeamId);
  const shownThreads = bucketThreads.filter((t) => !t.archived && !t.completed && !t.cancelled);
  const archivedThreads = bucketThreads.filter((t) => t.archived);
  const completedThreads = bucketThreads.filter((t) => t.completed && !t.archived);
  const cancelledThreads = bucketThreads.filter((t) => t.cancelled && !t.archived);
  // 選択中スレッドが「今のチームに実在」しなくなったら選択解除し、新規スレッドの空状態へ戻す。
  // 別チーム/削除済みスレッドを指したまま、メッセージ欄・入力欄・添付が前のスレッドのまま残る不整合を防ぐ
  // （社長指示 2026-08-08 / AI-WF330）。仮想バケット(BROADCAST/FAVORITES)は activeSession が別チーム/横断を
  // 正当に指す or null 固定なので対象外。読込完了後のみ判定（読込前は復元を尊重＝ちらつき/復元潰し防止）。
  const activeProjIdx = active ? list.findIndex((p) => p.projectId === active.projectId) : -1;
  const projThreadsLoaded = activeProjIdx >= 0 && threadQueries[activeProjIdx]?.data !== undefined;
  const bucketIsRealTeam = activeTeamId !== BROADCAST && activeTeamId !== FAVORITES;
  const activeSessionInBucket =
    activeSession != null && bucketThreads.some((t) => t.conversationId === activeSession);
  // 送信直後の新スレッドは bucketThreads 未確定だが pending(送信中)がある。
  // live または pending がある間はリセットしない（バックエンド確定前に強制 null されるのを防ぐ）。
  // task#450 probe 実装で楽観的 appendLive を外したため pending も判定に加える。
  const activeSessionHasLive = activeSession != null && (
    (liveByConv[activeSession]?.length ?? 0) > 0 ||
    (pendingByConv[activeSession] ?? 0) > 0
  );
  // 完了/中止/アーカイブセクションは折りたたみ可能。折りたたまれていると対象スレッドが描画されず
  // 「何も選択されていない」のに右パネルにメッセージが残る。展開状態と照合して可視かを判定する
  // （社長指示 2026-08-09）。
  const activeSessionIsVisible =
    activeSession != null && (
      shownThreads.some((t) => t.conversationId === activeSession) ||
      (showArchived && archivedThreads.some((t) => t.conversationId === activeSession)) ||
      (showCompleted && completedThreads.some((t) => t.conversationId === activeSession)) ||
      (showCancelled && cancelledThreads.some((t) => t.conversationId === activeSession))
    );
  useEffect(() => {
    if (!active || activeSession == null || !projThreadsLoaded || !bucketIsRealTeam) return;
    if (!activeSessionInBucket && !activeSessionHasLive) { setActiveSession(null); return; }
    // バケット内に存在するが折りたたまれて非表示のスレッドもリセット（右パネルに残るのを防ぐ）。
    if (!activeSessionIsVisible && !activeSessionHasLive) setActiveSession(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.projectId, activeSession, activeTeamId, projThreadsLoaded, bucketIsRealTeam, activeSessionInBucket, activeSessionHasLive, activeSessionIsVisible]);
  // お気に入り：チームをまたいで全スレッドから favorited のものを抽出（lastAt 降順）。
  const favoriteThreads = projThreads.filter((t) => t.favorited).sort((a, b) => new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime());
  // 全体お気に入り：プロジェクトもまたいで favorited を抽出（lastAt 降順）。
  // 全PJのスレッドは未読集計用に取得済み（threadsByProject）なので、ここで PJ名/チーム名を添えるだけ。
  const allFavoriteThreads = useMemo<GlobalFavThread[]>(() => {
    const out: GlobalFavThread[] = [];
    for (const p of list) {
      for (const t of threadsByProject.get(p.projectId) ?? []) {
        if (!t.favorited) continue;
        const team = p.teams.find((tm) => tm.id === t.teamId);
        out.push({
          ...t,
          projectId: p.projectId,
          projectName: p.name,
          teamName: team?.name ?? '未分類',
          jumpTeamId: team ? team.id : UNCLASSIFIED,
        });
      }
    }
    return out.sort((a, b) => new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime());
  }, [list, threadsByProject]);

  // 未読数（サーバ管理の unread を集計）。バケット(チーム/未分類)未読=配下和、プロジェクト未読=全配下和。
  const unreadByTeam = useMemo(() => {
    const m = new Map<number, number>();
    for (const t of projThreads) {
      const k = t.teamId != null && teamIdSet.has(t.teamId) ? t.teamId : UNCLASSIFIED;
      m.set(k, (m.get(k) ?? 0) + t.unread);
    }
    return m;
  }, [projThreads, teamIdSet]);
  const unreadByProject = useMemo(() => {
    const m = new Map<number, number>();
    for (const p of list) {
      const ts = threadsByProject.get(p.projectId) ?? [];
      m.set(p.projectId, ts.reduce((s, t) => s + t.unread, 0));
    }
    return m;
  }, [list, threadsByProject]);
  const msgsQ = useQuery({
    queryKey: ['thread-msgs', activeSession],
    queryFn: () => getThreadMessages(activeSession!),
    enabled: !!activeSession,
    // AI-WF251: スレッドを開く/切り替えるたびに必ず最新をDBから取り直す。
    // SSE は「今開いている会話」しか更新せず、裏に回した会話で終わった返信や
    // 取りこぼした chunk/final は反映されない。グローバル staleTime:30s のままだと
    // 開き直しても再取得されず「考え中」のまま固まる（ランプは点くのに本文が出ない）。
    // → staleTime:0 で開くたびに DB の最新（final 本文）を回収する。本文は run-chunk が
    //    逐次DB保存済みなので、streaming 中に開き直しても取りこぼさない。
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // ブロードキャスト一覧（仮想チーム用）。実体スレを broadcastId で束ねたグループ。
  // 全プロジェクト・ブロードキャスト時はテナント横断(listAllBroadcasts)、通常時はPJ単位。
  const broadcastsQ = useQuery({
    queryKey: ['broadcasts', active?.projectId],
    queryFn: () => (globalBC ? listAllBroadcasts() : listProjectBroadcasts(active!.projectId)),
    // 全体お気に入りの仮想PJ(-101)にはブロードキャストが無い（実PJ APIを叩かない）。
    enabled: !!active && !globalFav,
    refetchInterval: 20_000,
  });
  const broadcasts = broadcastsQ.data ?? [];
  const broadcastUnread = broadcasts.reduce((s, b) => s + b.unread, 0);
  // 一覧で展開中のブロードキャストグループ（配下スレを開いて見せる）。
  const [expandedBroadcast, setExpandedBroadcast] = useState<string | null>(null);

  // ── リアルタイム配信(SSE 1対N)。設計doc 10。スレッド一覧の20s 再取得は据置、
  // 開いている会話の本文だけ EventSource で push 反映する（旧ポーリング置換）。──
  // アクティブな会話に対して EventSource を開き、message/update イベントで
  // TanStack Query キャッシュ ['thread-msgs', conversationId] を直接更新する。
  // 別端末(PC/スマホ)の発言・AI返信・コマンド結果がポーリング無しでライブ反映される。
  const accessToken = useAuthStore((s) => s.accessToken);
  useEffect(() => {
    if (!activeSession || !accessToken) return;
    const conv = activeSession;
    const es = new EventSource(threadStreamUrl(conv, accessToken));

    const apply = (ev: ThreadStreamEvent) => {
      // AI-WF265 (要望#213): 通話系（src/call/）へも中継（非同期完了の着信リング副経路。受け側 call-dock.tsx）。
      window.dispatchEvent(new CustomEvent('konecco:thread-event', { detail: ev }));
      if (ev.conversationId !== conv) return;
      const key = ['thread-msgs', conv];
      if (ev.kind === 'message') {
        qc.setQueryData<ThreadMessage[]>(key, (prev) => {
          const list = prev ?? [];
          // 既に同 id があれば置換（再送/重複の安全側）。無ければ末尾に追加。
          const idx = list.findIndex((m) => m.id === ev.message.id);
          if (idx >= 0) {
            const next = list.slice();
            next[idx] = ev.message;
            return next;
          }
          return [...list, ev.message];
        });
      } else if (ev.kind === 'chunk') {
        // progress = 「今この依頼が動いているか」を見せる鼓動。本文(text)には混ぜず、
        // 進行中バブルの liveActivity に最新の作業内容を1行だけ載せる（要望：動いてる感の可視化）。
        // 以前はここで progress を捨てていた（見えなかった原因）。
        if (ev.chunkKind === 'progress') {
          const label = liveActivityLabel(ev.delta);
          if (!label) return;
          const now = new Date();
          const t = hms(now); // 受信時刻（client 近似）を鼓動行の時刻にする。
          const ms = now.getTime();
          // 永続 ref を master に追記（上書きしない）。直前と同一ラベルの連続（thinking の
          // 連打等）は行を増やさず末尾の時刻だけ更新（スパム防止）。ref は refetch で消えない。
          const entries = liveActivityRef.current.get(ev.id) ?? [];
          const last = entries[entries.length - 1];
          const nextList =
            last && last.label === label
              ? [...entries.slice(0, -1), { t, label, ms }]
              : [...entries, { t, label, ms }];
          liveActivityRef.current.set(ev.id, nextList);
          // キャッシュにも反映（即時の反応的再描画のため。別スレ往復後は shown 構築時に ref からマージ）。
          qc.setQueryData<ThreadMessage[]>(key, (prev) => {
            if (!prev) return prev;
            const idx = prev.findIndex((m) => m.id === ev.id);
            if (idx < 0) return prev; // 進行中バブル未着でも ref には積んである（次の再描画でマージ）。
            const next = prev.slice();
            next[idx] = { ...prev[idx], liveActivity: nextList };
            return next;
          });
          return;
        }
        // AI-WF251 (C1/C3): text 増分を進行中バブル(数値 id)へ append。
        // seq>lastSeq の delta だけ適用（冪等 dedup）。
        // AI-WF338 #268: per-token re-render 削減 — delta をバッファに積んで rAF でバッチ適用。
        {
          // 全量アキュムレータを更新（rAF flush で cur.text+delta でなく acc.text を使うため）。
          const curAcc = streamAccumRef.current.get(ev.id) ?? { text: '', lastSeq: 0 };
          // SSE 再接続による欠落チャンク対応: DB キャッシュがアキュムレータ以上に進んでいる場合は
          // DB キャッシュをベースにする（再接続中に backend が保存した分を DB から補完）。
          const cacheMsg = qc.getQueryData<ThreadMessage[]>(key)?.find((m) => m.id === ev.id);
          const cacheSeq = cacheMsg?.lastSeq ?? 0;
          const baseAcc =
            cacheSeq >= curAcc.lastSeq && cacheMsg
              ? { text: cacheMsg.text ?? '', lastSeq: cacheSeq }
              : curAcc;
          if (ev.seq > baseAcc.lastSeq) {
            streamAccumRef.current.set(ev.id, { text: baseAcc.text + ev.delta, lastSeq: ev.seq });
          }
          const prev = chunkBufRef.current.get(ev.id);
          chunkBufRef.current.set(ev.id, {
            delta: (prev?.delta ?? '') + ev.delta,
            seq: ev.seq,
          });
          if (!flushRafRef.current) {
            flushRafRef.current = requestAnimationFrame(() => {
              flushRafRef.current = null;
              const snap = new Map(chunkBufRef.current);
              chunkBufRef.current.clear();
              qc.setQueryData<ThreadMessage[]>(key, (msgs) => {
                if (!msgs) return msgs;
                let changed = false;
                const next = msgs.slice();
                for (const [id] of snap) {
                  const idx = next.findIndex((m) => m.id === id);
                  if (idx < 0) {
                    void qc.invalidateQueries({ queryKey: key });
                    continue;
                  }
                  const cur = next[idx];
                  // 全量アキュムレータから書く（差分 delta でなく全量で上書き）。
                  // こうすれば refetch でキャッシュが古い状態に巻き戻っても次の flush で正しく復元される。
                  const acc = streamAccumRef.current.get(id);
                  if (!acc || acc.lastSeq <= (cur.lastSeq ?? 0)) continue;
                  next[idx] = {
                    ...cur,
                    text: acc.text,
                    lastSeq: acc.lastSeq,
                    streamState: 'streaming',
                  };
                  changed = true;
                }
                return changed ? next : msgs;
              });
              // streaming 中はメッセージ件数が増えないので useEffect の追従が動かない。
              // rAF フラッシュ後に下端付近なら直接スクロールして補う。
              if (atBottomRef.current) {
                const el = logRef.current;
                if (el) el.scrollTop = el.scrollHeight;
              }
            });
          }
        }
      } else if (ev.kind === 'seq-reset') {
        // task#450: probe→実run 切替。FE の lastSeq を 0 にリセットして実 run の seq=1 が通るようにする。
        streamAccumRef.current.delete(ev.id); // probe 分の蓄積をクリア（実 run は 0 から始まる）。
        qc.setQueryData<ThreadMessage[]>(key, (prev) => {
          if (!prev) return prev;
          const idx = prev.findIndex((m) => m.id === ev.id);
          if (idx < 0) return prev;
          const next = prev.slice();
          next[idx] = { ...prev[idx], lastSeq: 0 };
          return next;
        });
      } else if (ev.kind === 'update') {
        // AI-WF251: final 確定なら次メッセージ用に session_id を控える。
        if (ev.streamState === 'final') {
          streamAccumRef.current.delete(ev.id); // streaming 完了、アキュムレータを解放。
          if (ev.sessionId) resumeRef.current.set(conv, ev.sessionId);
          // AI-WF251: いま開いている会話に返信が届いた瞬間に既読化する。
          // 未読は listThreads(20s ポーリング)由来で、自動既読も projThreads 依存のため、
          // 見ているスレッドに一時的に未読バッジが付き（別スレへ移ると残留）、
          // 「見たはずのスレに未読が付く」現象になる。SSE final=いま開いている会話 なので即既読。
          // markThreadRead は成功時に ['threads'] を invalidate するので、
          // 同時に裏スレの未読反映も前倒しされる。
          void markThreadRead(conv)
            .then(() => qc.invalidateQueries({ queryKey: ['threads'] }))
            .catch(() => {});
          // streaming 中の崩れた表示を DB 確定テキストで即時上書き。
          // final 前は rAF chunk 積み上げで表示が乱れることがあるが、
          // DB 側は run-chunk ごとに正しく append 済みなので refetch で解消する。
          void qc.invalidateQueries({ queryKey: ['thread-msgs', conv] });
        }
        // status/actions/streamState 差分を該当メッセージ(数値 id)へ in-place パッチ。
        qc.setQueryData<ThreadMessage[]>(key, (prev) => {
          if (!prev) return prev;
          const idx = prev.findIndex((m) => m.id === ev.id);
          if (idx < 0) {
            // キャッシュに未着（取りこぼし）の場合のみ取り直す。
            void qc.invalidateQueries({ queryKey: key });
            return prev;
          }
          const next = prev.slice();
          next[idx] = {
            ...next[idx],
            ...(ev.status !== undefined
              ? { status: ev.status as ThreadMessage['status'] }
              : {}),
            ...(ev.actions !== undefined ? { actions: ev.actions } : {}),
            ...(ev.streamState !== undefined ? { streamState: ev.streamState } : {}),
            // final でも鼓動ログは消さず残す（debug 用・追記方針）。緑カーソルは streamState=final で止まる。
            ...(ev.stopReason !== undefined ? { stopReason: ev.stopReason } : {}),
            // タスク#408: final で確定した sessionId を run 識別子表示（sessionId-requestId）に反映。
            ...(ev.sessionId !== undefined ? { sessionId: ev.sessionId } : {}),
          };
          return next;
        });
      }
    };

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data) as ThreadStreamEvent | { kind: 'ready' | 'ping' };
        const k = (data as { kind: string }).kind;
        // ready=接続足場 / ping=ハートビート（接続維持のためだけの空イベント）。表示に使わない。
        if (k === 'ready' || k === 'ping') return;
        apply(data as ThreadStreamEvent);
      } catch {
        /* 不正ペイロードは無視（接続は維持） */
      }
    };
    // catch-up: 接続が(再)確立するたびに開いている会話の本文を取り直す。
    // SSE は取りこぼし再送が無いため、無音切断中に配信された message/final を
    // 復旧できず「送信するまで反映されない(=止まって見える)」現象になる。
    // 初回 open は既存ロードと重複するが、再接続時にここが欠落分を DB から埋める。
    // 出所=返信が止まって見える現象の調査（backend ハートビート/反バッファリングと対）。
    es.onopen = () => {
      void qc.invalidateQueries({ queryKey: ['thread-msgs', conv] });
    };
    // EventSource は切断時に自動再接続する。明示的なエラーログは出さない（再接続に任せる）。
    es.onerror = () => {
      /* auto-reconnect by EventSource → 復帰時に onopen が catch-up する */
    };

    return () => {
      es.close();
      // AI-WF338 #268: conv が切り替わるときにバッファとタイマーをリセット（古い conv の残留防止）。
      if (flushRafRef.current !== null) {
        cancelAnimationFrame(flushRafRef.current);
        flushRafRef.current = null;
      }
      chunkBufRef.current.clear();
      streamAccumRef.current.clear();
    };
  }, [activeSession, accessToken, qc]);

  // プロジェクトごとの選択が確定済みか（既定バケット解決の上書き防止に使う）。
  const navInitRef = useRef<number | null>(null);

  // 選択を確定する小さなヘルパー（保存もここで行う＝トーナメント型の記憶）。
  const pickThread = (conversationId: string | null) => {
    // 表示を切り替えるだけ。下書き/送信中/楽観表示は会話キーで分離済みなので消さない。
    setActiveSession(conversationId);
    if (active) {
      setLastThread(active.projectId, activeTeamId, conversationId);
      navInitRef.current = active.projectId; // ユーザー操作＝確定
    }
  };
  const pickTeam = (teamId: number) => {
    setActiveView('threads'); // チームを選んだらスレッド表示へ戻す
    if (teamId === activeTeamId) return; // 同じバケットの再クリックは無視
    setActiveTeamId(teamId);
    if (active) {
      setLastTeam(active.projectId, teamId);
      navInitRef.current = active.projectId; // ユーザー操作＝確定
      // そのチームで最後に見ていたスレッドを復元（無ければ新しいスレッド=null）。
      setActiveSession(getLastThread(active.projectId, teamId));
    } else {
      setActiveSession(null);
    }
  };

  // プロジェクトを開いたら、そのプロジェクトの前回チーム＋そのチームの前回スレッドを復元。
  // チームが未確定なら、スレッド読込後に「最新スレッドのあるバケット」を既定にする（履歴が見える）。
  useEffect(() => {
    if (!active) return;
    const pid = active.projectId;
    setLastProject(pid);
    setMention(null);
    setMentionRef(null);
    // 全プロジェクト・ブロードキャストはチーム概念が無いので BROADCAST に固定（新規一斉送信の構え）。
    if (globalBC) {
      setActiveTeamId(BROADCAST);
      setActiveSession(null);
      navInitRef.current = pid;
      return;
    }
    // 全体お気に入りも同様にチーム概念が無いので FAVORITES に固定（横断一覧の表示）。
    if (globalFav) {
      setActiveTeamId(FAVORITES);
      setActiveSession(null);
      navInitRef.current = pid;
      return;
    }
    const savedTeam = getLastTeam(pid);
    const teamValid =
      savedTeam != null &&
      (savedTeam === UNCLASSIFIED ||
        savedTeam === BROADCAST ||
        savedTeam === FAVORITES ||
        active.teams.some((t) => t.id === savedTeam));
    if (teamValid) {
      setActiveTeamId(savedTeam!);
      setActiveSession(getLastThread(pid, savedTeam!));
      navInitRef.current = pid; // 確定
    } else {
      // 暫定で先頭チーム（古い状態の保存を避ける）。スレッド読込後に既定バケットへ寄せる。
      const provisional = active.teams[0]?.id ?? UNCLASSIFIED;
      setActiveTeamId(provisional);
      setActiveSession(getLastThread(pid, provisional));
      navInitRef.current = null; // 未確定
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.projectId]);

  // スレッド読込後、保存が無いプロジェクトは「最新スレッドのあるバケット」を既定選択にする。
  useEffect(() => {
    if (!active) return;
    const pid = active.projectId;
    if (navInitRef.current === pid) return; // すでに確定
    const ts = threadsByProject.get(pid);
    if (ts === undefined) return; // 読込待ち
    const bucket = ts.length ? effTeam(ts[0]) : active.teams[0]?.id ?? UNCLASSIFIED;
    setActiveTeamId(bucket);
    setActiveSession(getLastThread(pid, bucket));
    setLastTeam(pid, bucket);
    navInitRef.current = pid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.projectId, threadsByProject]);

  // ヘッダー全件検索からの遷移を処理：該当PJ/チーム/スレッドを開く。localStorage に先に積むことで、
  // プロジェクト切替の復元 effect が同じ選択を復元する（ちらつき防止）。処理後は URL パラメータを消す。
  useEffect(() => {
    const open = searchParams.get('open');
    if (!open) return;
    if (list.length === 0) return; // プロジェクト読込待ち
    const pid = Number(searchParams.get('pj'));
    const teamRaw = searchParams.get('team');
    const team = teamRaw ? Number(teamRaw) : UNCLASSIFIED;
    if (Number.isFinite(pid)) {
      setLastProject(pid);
      setLastTeam(pid, team);
      setLastThread(pid, team, open);
      navInitRef.current = pid;
      setActiveId(pid);
      setActiveTeamId(team);
      setActiveSession(open);
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, list.length]);

  // 開いているスレッドに未読があればサーバへ既読を記録（開いた瞬間＋新着到着時）。
  // ＝「見ているスレッド」の新着は即既読にしてバッジを出さない。未読が残るのは"開いていない"スレッドだけ。
  // （未読カウントは backend 側で「相手(AI)のメッセージのみ」に修正済み。自分の送信では増えない。）
  useEffect(() => {
    if (!activeSession) return;
    const th = projThreads.find((t) => t.conversationId === activeSession);
    if (th && th.unread > 0 && !markReadMut.isPending) markReadMut.mutate(activeSession);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession, projThreads]);

  // スレッドを開いたら表示位置の復元/未読ジャンプを予約する。
  // ref の初期化は useLayoutEffect で行い、スクロール復元が useEffect より先に動けるようにする。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!activeSession) {
      pendingPosRef.current = null;
      scrollLockRef.current = false;
      needsUnreadScrollRef.current = false;
      return;
    }
    // 未読件数は既読化より前に確定させるため projThreads から取る（stale 許容）。
    const th = projThreads.find((t) => t.conversationId === activeSession);
    unreadCountRef.current = th?.unread ?? 0;
    pendingPosRef.current = activeSession;
    scrollLockRef.current = true;
    needsUnreadScrollRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession]);

  // 状態リセット（divider / ジャンプボタン）はスクロール復元と独立して行う。
  useEffect(() => {
    if (!activeSession) {
      setUnreadAnchorIdx(null);
      return;
    }
    // スクロール復元がキャッシュデータで即完了した場合は pendingPosRef が null になっている。
    // その場合は unreadAnchorIdx を layout effect 側で設定済みなので上書きしない。
    if (pendingPosRef.current === activeSession) setUnreadAnchorIdx(null);
    setShowJump(false);
    setJumpCount(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession]);

  // メッセージ読込完了後に位置決め: 未読があれば最初の未読へ、無ければ前回スクロール位置（無ければ最下部）へ。
  // useLayoutEffect を使い DOM commit 直後・ブラウザ描画前に同期実行することで
  // 「最上部が一瞬見えてから最下部へジャンプ」するフラッシュを防ぐ。
  useLayoutEffect(() => {
    const conv = activeSession;
    if (!conv || pendingPosRef.current !== conv) return;
    if (msgsQ.data === undefined) return; // メッセージ読込待ち
    const msgs = msgsQ.data;
    const unread = unreadCountRef.current;
    if (unread > 0) {
      // divider が描画されるよう state 更新し、useLayoutEffect([unreadAnchorIdx]) でスクロール。
      const idx = Math.max(0, msgs.length - unread); // 最初の未読＝末尾から unread 件目
      setUnreadAnchorIdx(idx);
      needsUnreadScrollRef.current = true;
    } else {
      setUnreadAnchorIdx(null);
      const el = logRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
        atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
      }
      scrollLockRef.current = false;
    }
    pendingPosRef.current = null;
  }, [activeSession, msgsQ.data]);

  // 未読ジャンプ: divider が DOM に描画された後（unreadAnchorIdx 変化後）に同期スクロール。
  useLayoutEffect(() => {
    if (!needsUnreadScrollRef.current) return;
    const d = unreadDividerRef.current;
    const el = logRef.current;
    if (!d || !el) return;
    el.scrollTop = Math.max(0, d.offsetTop - 16);
    scrollLockRef.current = false;
    needsUnreadScrollRef.current = false;
    // 未読位置へジャンプ後、最下部でなければ↓ボタンを表示する。
    if (el.scrollHeight - el.scrollTop - el.clientHeight >= 120) {
      setShowJump(true);
    }
  }, [unreadAnchorIdx]);

  // 新着追従: メッセージが増えたら（送信 or SSE着信）、下端付近に居る時だけ最下部へスクロール。
  // （履歴を遡って読んでいる時は勝手に飛ばさない＝普通のチャット挙動）
  useEffect(() => {
    const el = logRef.current;
    if (!el || !activeSession) return;
    const count = (msgsQ.data?.length ?? 0) + live.length;
    const grew = count > stickBottomRef.current;
    const prevCount = stickBottomRef.current;
    stickBottomRef.current = count;
    if (scrollLockRef.current) return; // 位置復元中は触らない
    if (!grew) return;
    // 新着が入って画面が伸びる「前」に下端付近だったか（onScroll で記録）で判定する。
    // 着信後の高さで判定すると、長い返信のぶん gap が開いて追従しなくなるため。
    if (atBottomRef.current) {
      requestAnimationFrame(() => {
        const e2 = logRef.current;
        if (e2) e2.scrollTop = e2.scrollHeight;
      });
    } else {
      // 上に居る間に新着が届いた → ↓ボタンを出してカウントを増やす
      setShowJump(true);
      setJumpCount((c) => c + (count - prevCount));
    }
  }, [activeSession, msgsQ.data, live.length]);

  // グローバルヘッダー（設定の横）のパンくずへ、選択中の プロジェクト/チーム/スレッド 名を吸い上げる。
  const setBreadcrumb = useBreadcrumbStore((s) => s.setBreadcrumb);
  useEffect(() => {
    if (!active) {
      setBreadcrumb(null);
      return;
    }
    const team = active.teams.find((t) => t.id === activeTeamId);
    const thread = activeSession
      ? projThreads.find((t) => t.conversationId === activeSession)
      : null;
    const teamCrumb = team
      ? teamLabel(team)
      : activeTeamId === UNCLASSIFIED
        ? '未分類'
        : 'チーム未選択';
    setBreadcrumb({
      project: active.name,
      team: teamCrumb,
      thread: activeSession ? thread?.title || '(無題)' : '新しいスレッド',
    });
  }, [active, activeTeamId, activeSession, projThreads, setBreadcrumb]);

  // チャット画面を離れたらパンくずを消す。
  useEffect(() => () => setBreadcrumb(null), [setBreadcrumb]);

  // ブロードキャスト送信: 対象の全チーム（オンラインDIRを持つチーム）へ、各チームに新規スレッドを
  // 1本ずつ立てて同じ文面を一斉発信する。共通の broadcastId で束ねる。専用レコードは作らず、
  // 痕跡は各実体スレ側にだけ残る。返信は各実体スレで完結（ここへは集約しない）。
  // 対象は、通常時=選択中PJの全チーム / 全プロジェクト時=全PJの全チーム。
  const sendBroadcast = async () => {
    const proj = active;
    if (!proj) return;
    const typedKey = `new:${proj.projectId}:${BROADCAST}`;
    const text = (draftByKey[typedKey] ?? '').trim();
    const atts = attachByKey[typedKey] ?? [];
    if (!text && atts.length === 0) return;
    // 対象を {projectId, teamId, pc} のフラットなリストで作る（pc は実PJの値を保持）。
    const sourceProjects = globalBC ? list : [proj];
    const targets = sourceProjects.flatMap((p) =>
      p.teams
        .map((t) => ({
          projectId: p.projectId,
          teamId: t.id,
          pc: p.pcs.find((pc) => pc.teamIds.includes(t.id) && pc.online),
        }))
        .filter((x): x is { projectId: number; teamId: number; pc: Pc } => !!x.pc),
    );
    if (targets.length === 0) {
      // ブロードキャストには会話ログが無いので、ここだけは alert で知らせる（下書きは残す）。
      window.alert('一斉発信できるチームがありません（オンラインのDIRを持つチームが無い）。');
      return;
    }
    const promptText = text || '画像を送信しました';
    const broadcastId = `bc_${genUUID()}`;

    // 添付は1回だけアップロードし、全チームで同じ fileId を使い回す。
    let uploaded: { fileId: string; name: string; mime: string }[] | undefined;
    if (atts.length) {
      try {
        const up = await uploadChatFiles(atts.map((a) => a.file));
        uploaded = up.map((u) => ({ fileId: u.fileId, name: u.name, mime: u.mime }));
      } catch {
        window.alert('添付画像のアップロードに失敗しました。');
        return;
      }
    }
    // 入力と添付をクリアし、新グループを開いた状態にする。
    setDraftByKey((d) => ({ ...d, [typedKey]: '' }));
    setAttachByKey((m) => ({ ...m, [typedKey]: [] }));
    setExpandedBroadcast(broadcastId);

    for (const { projectId, teamId, pc } of targets) {
      const conv = `conv_${genUUID()}`;
      bumpPending(conv, +1);
      const prevQ = queuesRef.current.get(conv) ?? Promise.resolve();
      const nextQ = prevQ.then(async () => {
        try {
          await consoleChat(pc.deviceId, {
            projectId,
            cwd: pc.cwd,
            prompt: promptText,
            conversationId: conv,
            teamId,
            broadcastId,
            attachments: uploaded,
          });
        } catch {
          /* 個別チームの失敗は一覧の再取得で状況が見える（撃ちっぱなし） */
        } finally {
          bumpPending(conv, -1);
          // 表示中のブロードキャスト一覧（PJ単位 or 全PJ）と、対象PJのスレッド一覧を更新。
          await qc.invalidateQueries({ queryKey: ['broadcasts', proj.projectId] });
          await qc.invalidateQueries({ queryKey: ['threads', projectId] });
        }
      });
      queuesRef.current.set(conv, nextQ.catch(() => {}));
    }
  };

  // 配下の実体スレを開く（ブロードキャスト束ねから、そのPJ/チームの本スレッドへ移動）。
  // 全プロジェクト版では別PJへ飛ぶので projectId も切り替える。localStorage に先に積むことで、
  // プロジェクト切替で走る復元 effect が同じチーム/スレッドを復元する（ちらつき防止）。
  const openBroadcastChild = (
    projectId: number,
    teamId: number | null,
    conversationId: string,
  ) => {
    const team = teamId ?? UNCLASSIFIED;
    setLastProject(projectId);
    setLastTeam(projectId, team);
    setLastThread(projectId, team, conversationId);
    navInitRef.current = projectId;
    setActiveId(projectId);
    setActiveTeamId(team);
    setActiveSession(conversationId);
    if (isMobile) setMobileLevel(4);
  };

  // 送信は非ブロッキング。会話(スレッド)ごとの送信キューに積み、UIは固めない。
  // 別スレッドへ自由に移動でき、各スレッドは独立・並列に応答が返る。
  const send = () => {
    if (activeTeamId === BROADCAST) { void sendBroadcast(); return; }
    const text = input.trim();
    const proj = active;
    const teamId0 = activeTeamId;
    if (!proj) return;
    const typedKey = activeSession ?? `new:${proj.projectId}:${teamId0}`;
    const atts = attachByKey[typedKey] ?? [];
    // テキストか画像のどちらかがあれば送信可（画像のみも可・LINE風）。
    if (!text && atts.length === 0) return;
    const toRef = mentionRef ?? undefined; // @無し＝チームPM既定（undefined）
    // /cmd args のパース（境界契約 §3.1）。@mention と併存。先頭/直後の空白の後の "/name" を1つ拾う。
    // FE は name(先頭/抜き)と args を抽出して送るだけ。種別(kind)解決は Backend（FEは判断しない）。
    let commandName: string | undefined;
    let commandArgs: string | undefined;
    const cmdMatch = text.match(/(?:^|\s)\/([^\s/]+)(?:\s+([\s\S]*))?$/);
    if (cmdMatch) {
      commandName = cmdMatch[1];
      const rest = (cmdMatch[2] ?? '').trim();
      commandArgs = rest || undefined;
    }
    // バックエンドは prompt 必須なので、画像のみのときは控えめなプレースホルダを入れる。
    const promptText = text || '画像を送信しました';
    // 新スレッドは conversationId をクライアント発番（連投が同じ会話に束なるように）。
    const isNew = !activeSession;
    const conv = activeSession ?? `conv_${genUUID()}`;

    // 入力と添付プレビューをクリア＋宛先確定解除。新スレッドは即この会話を選択して連投を束ねる。
    // 添付の ObjectURL は下の楽観表示へ引き継ぐのでここでは revoke しない。
    setDraftByKey((d) => ({ ...d, [typedKey]: '' }));
    setAttachByKey((m) => ({ ...m, [typedKey]: [] }));
    setMention(null);
    setMentionRef(null);
    if (isNew) {
      setActiveSession(conv);
      setLastThread(proj.projectId, teamId0, conv);
    }
    // task#450 先OKハンドシェイク: ユーザーメッセージは probe 完了後にサーバが作成するため
    // 楽観的即時表示はしない（probe → OK → ユーザーメッセージ → AI応答 の順で表示される）。

    // @コントローラは未対応（システム宛て）。注意書きを出して終わり。
    if (toRef === 'controller') {
      appendLive(conv, { role: 'system', text: '@コントローラ（システムのコントローラ）宛ては未対応です。システム側に Claude Code を入れて function calling で受ける構成ができてから有効になります（例: 全員に一斉停止 などのシステム全体指示）。' });
      return;
    }

    // 実行先：実チーム選択時はそのチームの DIR、未分類はプロジェクト全体から、オンライン1台。
    const realTeamId = teamId0 != null && teamId0 !== UNCLASSIFIED ? teamId0 : null;
    const teamPcs = realTeamId != null ? proj.pcs.filter((p) => p.teamIds.includes(realTeamId)) : proj.pcs;
    const pc = teamPcs.find((p) => p.online);
    if (!pc) {
      const teamName = proj.teams.find((t) => t.id === realTeamId)?.name;
      appendLive(conv, {
        role: 'system',
        text: realTeamId != null
          ? `「${teamName ?? 'このチーム'}」のPC（DIR）が今オフラインです。`
          : 'このプロジェクトに応答できるPC（DIR）が今ありません。チームを選ぶか、設定でDIRを割り当ててください。',
      });
      return;
    }

    // @メンバー（サブエージェント）宛て：PM 経由で本人へ伝えるルーティング前置き。
    let prompt = promptText;
    if (toRef?.startsWith('agent:')) {
      const cand = mentionCandidates.find((c) => c.ref === toRef);
      const agentId = Number(toRef.slice('agent:'.length));
      if (cand && pmAgent?.id !== agentId) {
        prompt = `【ルーティング】ユーザーはサブエージェント @${cand.label} に直接伝えています。PM から ${cand.label} に伝えてください。\n\n${promptText}`;
      }
    }

    // 会話ごとのキューに積む（同一スレッドは順次、別スレッドは並行）。
    // AI-WF362: 既に pending がある（busy=true）ならこの送信は待機状態 → バブル表示。
    if ((pendingByConv[conv] ?? 0) > 0) {
      setWaitBubbleByConv((m) => ({ ...m, [conv]: promptText }));
    }
    bumpPending(conv, +1);
    const prevQ = queuesRef.current.get(conv) ?? Promise.resolve();
    const nextQ = prevQ.then(async () => {
      // AI-WF362: このメッセージの送信キューが起動 = 待機終了 → バブルをクリア。
      setWaitBubbleByConv((m) => ({ ...m, [conv]: null }));
      const resume = resumeRef.current.get(conv)
        ?? projThreads.find((t) => t.conversationId === conv)?.sessionId
        ?? undefined;
      try {
        // 添付画像があれば先にアップロードして fileId を得る。
        let uploaded: { fileId: string; name: string; mime: string }[] | undefined;
        if (atts.length) {
          const up = await uploadChatFiles(atts.map((a) => a.file));
          uploaded = up.map((u) => ({ fileId: u.fileId, name: u.name, mime: u.mime }));
        }
        const res = await consoleChat(pc.deviceId, {
          projectId: proj.projectId,
          cwd: pc.cwd,
          prompt,
          conversationId: conv,
          resumeSessionId: resume ?? undefined,
          teamId: realTeamId ?? undefined,
          toRef,
          attachments: uploaded,
          commandName,
          commandArgs,
          // AI-WF262 (要望#211): 📮非同期モード。ON のときだけ async:true（OFF は従来同期＝キー自体送らない）。
          async: asyncMode || undefined,
        });
        if (res.sessionId) resumeRef.current.set(conv, res.sessionId);
        if (!res.delivered) {
          appendLive(conv, { role: 'system', text: `未配信（${res.reason ?? 'device-offline'}）` });
        }
        // サーバに user/ai が確定 → 再取得。
        await qc.invalidateQueries({ queryKey: ['threads', proj.projectId] });
        await qc.invalidateQueries({ queryKey: ['thread-msgs', conv] });
      } catch (e) {
        appendLive(conv, { role: 'system', text: e instanceof Error ? e.message : '通信エラー' });
      } finally {
        bumpPending(conv, -1);
      }
    });
    queuesRef.current.set(conv, nextQ.catch(() => {}));
  };

  // タスク#408: 指定 run をメッセージ単位で中断する（最新以外の実行中 run も止められる）。
  // Worker は childMap を requestId で引いて該当 claude 子プロセスだけを kill する。
  const cancelRun = async (deviceId: number, requestId: string) => {
    try {
      await cancelChat(deviceId, requestId);
      // run-done(cancelled) の SSE で該当メッセージが final(cancelled) に落ちる。
    } catch {
      /* オフライン等は BE 側でローカル cancelled 確定・SSE 反映される */
    }
  };

  // value のキャレット直前から、空白を含まない @ または / トークンを検出（行頭/空白の直後のみ有効）。
  const detectMention = (value: string, caret: number) => {
    const upto = value.slice(0, caret);
    const m = upto.match(/(^|\s)([@/])([^\s@/]*)$/);
    if (!m) return null;
    return { kind: m[2] as '@' | '/', query: m[3], start: caret - m[3].length - 1 };
  };
  const onComposerChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInput(value);
    if (!value.includes('@')) setMentionRef(null); // @が消えたら宛先確定も解除
    const caret = e.target.selectionStart ?? value.length;
    setMention(detectMention(value, caret));
    setMentionIndex(0); // クエリが変わったらハイライトを先頭へ戻す
  };
  // 候補選択：トークン(@xxx)を「@ラベル 」に置換し、宛先(ref)を確定する。
  const pickMention = (cand: { ref: string; label: string }) => {
    if (!mention) return;
    const before = input.slice(0, mention.start);
    const after = input.slice(mention.start + 1 + mention.query.length);
    const inserted = `@${cand.label} `;
    setInput(before + inserted + after);
    setMentionRef(cand.ref);
    setMention(null);
    const pos = before.length + inserted.length;
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (ta) { ta.focus(); ta.setSelectionRange(pos, pos); }
    });
  };
  const filteredMentions = mention?.kind === '@'
    ? mentionCandidates.filter((c) => c.label.toLowerCase().includes(mention.query.toLowerCase()))
    : [];
  // / コマンド候補：name/label の部分一致（クエリ空なら全件）。表示は backend 合成済み一覧から。
  const filteredCommands = mention?.kind === '/'
    ? availCommands.filter((c) => {
        const q = mention.query.toLowerCase();
        return !q || c.name.toLowerCase().includes(q) || c.label.toLowerCase().includes(q);
      })
    : [];
  // / コマンド選択：トークン(/xxx)を「/name 」に置換する（宛先 ref は変えない）。
  const pickCommand = (cmd: { name: string }) => {
    if (!mention) return;
    const before = input.slice(0, mention.start);
    const after = input.slice(mention.start + 1 + mention.query.length);
    const inserted = `/${cmd.name} `;
    setInput(before + inserted + after);
    setMention(null);
    const pos = before.length + inserted.length;
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (ta) { ta.focus(); ta.setSelectionRange(pos, pos); }
    });
  };

  const serverMsgs = activeSession ? msgsQ.data ?? [] : [];
  const shown: (Live & {
    fromRef?: string | null;
    atts?: DispAtt[];
    status?: ThreadMessage['status'];
    actions?: ThreadAction[];
    // AI-WF251 (streaming): lifecycle・終端理由（クルクル/返信待ち表示に使う）。
    streamState?: ThreadMessage['streamState'];
    stopReason?: string | null;
    // AI-WF262 (要望#211): 非同期の受領/作業中/完了チップ。
    msgKind?: ThreadMessage['msgKind'];
    // 「動いてる」鼓動ログ（時刻つき・追記）。
    liveActivity?: ThreadMessage['liveActivity'];
    // メッセージ日時（サーバ createdAt）。送信/返信の時刻表示に使う。
    createdAt?: string;
    // タスク#408: run 識別子（sessionId-requestId 表示）とメッセージ単位 cancel 用。
    sessionId?: string | null;
    requestId?: string | null;
    deviceId?: number | null;
    // task#450: probe OK バブルフラグ。
    isProbe?: boolean;
    // AI-WF362: 送信待機中バブル（busy 中に送信した C）。
    isWaiting?: boolean;
  })[] = [
    ...serverMsgs.map((m) => ({
      role: m.role,
      text: m.text,
      fromRef: m.fromRef,
      // メッセージ日時（サーバ時刻）。ユーザー送信/AI返信の時刻を出す。
      createdAt: m.createdAt,
      // タスク#408: run 識別子。表示は sessionId-requestId、cancel は requestId+deviceId で行う。
      sessionId: m.sessionId,
      requestId: m.requestId,
      deviceId: m.deviceId,
      // コマンド/実行の結果・会話状態（境界契約 §3.2）。描画でバッジ/出力を出す。
      status: m.status,
      actions: m.actions,
      // AI-WF251: streaming 状態と終端理由。
      streamState: m.streamState,
      stopReason: m.stopReason,
      // AI-WF262: 非同期メッセージ種別（ack/progress/final）。
      msgKind: m.msgKind,
      // task#450: probe OK バブルフラグ。
      isProbe: m.isProbe,
      // 「動いてる」鼓動（クライアント専用・progress chunk 由来）。
      // 別スレッド往復後の refetch では m.liveActivity は消えるので、永続 ref からマージして残す。
      liveActivity: m.liveActivity ?? liveActivityRef.current.get(m.id) ?? null,
      // 添付画像（サーバ確定）。fileId で認証付き取得して表示する。
      atts: (m.attachments ?? []).map((a) => ({
        key: a.fileId, name: a.name, mime: a.mime, fileId: a.fileId,
      })),
    })),
    ...live,
    // AI-WF362: busy 中に送信した C を待機バブルとして末尾に表示。
    ...(activeSession && (waitBubbleByConv[activeSession] ?? null)
      ? [{ role: 'user' as Live['role'], text: `⌛ 送信待機中：${waitBubbleByConv[activeSession]}`, isWaiting: true }]
      : []),
  ].filter((m) => !('isProbe' in m && (m as { isProbe?: boolean }).isProbe));
  return (
    <div style={S.wrap}>
      {/* 1) プロジェクト */}
      <aside style={isMobile ? (mobileLevel !== 1 ? { display: 'none' } : { ...S.side, width: '100%' }) : { ...S.side, width: sideWidth }}>
        <div style={S.workspace}>
          <span style={S.wsName}>チャット</span>
        </div>
        <div style={S.sideScroll}>
          {/* プロジェクト見出し（クリックで一覧を折りたたみ）。 */}
          <button style={S.sectionHeadBtn} onClick={() => setShowProjects((v) => !v)}>
            <span style={{ width: 12, display: 'inline-block' }}>{showProjects ? '▾' : '▸'}</span>
            プロジェクト
          </button>
          {showProjects && (
            <>
              {/* 全体お気に入り（プロジェクト配下の最上段・固定）。DBに作らない仮想プロジェクト。
                  チーム列のお気に入りと同じく、登録が1件でもあるときだけ出す。 */}
              {allFavoriteThreads.length > 0 && (
                <button
                  onClick={() => { setActiveId(GLOBAL_FAV_ID); if (isMobile) setMobileLevel(3); }}
                  style={{ ...S.chan, ...(globalFav ? S.chanActive : null) }}
                  title="全プロジェクトのお気に入りスレッド一覧"
                >
                  <span style={{ ...S.dragHandle, opacity: 0 }} aria-hidden>⠿</span>
                  <span style={{ ...S.chanText, flex: 1 }}>
                    <span style={S.chanName}><span style={{ color: '#f59e0b' }}>★</span> お気に入り</span>
                    <span style={S.chanWho}>全PJのお気に入り一覧</span>
                  </span>
                  <Badge n={allFavoriteThreads.reduce((s, t) => s + t.unread, 0)} />
                </button>
              )}
              {/* 全プロジェクト・ブロードキャスト（プロジェクト配下の先頭・固定）。DBに作らない仮想プロジェクト。 */}
              {list.length > 0 && (
                <button
                  onClick={() => { setActiveId(GLOBAL_BC_ID); if (isMobile) setMobileLevel(2); }}
                  style={{ ...S.chan, ...(globalBC ? S.chanActive : null) }}
                  title="全プロジェクトの全チームへ、新しいスレッドで一斉発信します"
                >
                  <span style={{ ...S.dragHandle, opacity: 0 }} aria-hidden>⠿</span>
                  <span style={{ ...S.chanText, flex: 1 }}>
                    <span style={S.chanName}>📢 全プロジェクト</span>
                    <span style={S.chanWho}>全PJの全チームへ一斉発信</span>
                  </span>
                  <Badge n={globalBC ? broadcastUnread : 0} />
                </button>
              )}
              {list.length === 0 && (
                <div style={S.sideNote}>
                  まだありません。<strong style={{ color: '#fff' }}>設定</strong> でプロジェクトに PC・DIR を割り当て。
                </div>
              )}
              {/* プロジェクトは複数デバイスをまたぐため死活ランプは出さない（死活はチーム列で見る）。 */}
              {/* アクティブ区画：type別グループ（dev/company/private）。区画末尾へのドロップも受ける。 */}
              <div
                onDragOver={(e) => { if (dragId != null) e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); onDropOn(null, false); }}
              >
                {(['dev', 'company', 'private'] as const).map((typ) => {
                  const typeLabel: Record<string, string> = { dev: '開発', company: 'カンパニー', private: 'プライベート' };
                  const group = list.filter((c) => !c.archived && c.type === typ);
                  if (group.length === 0) return null;
                  const collapsed = collapsedTypes.has(typ);
                  const toggleCollapse = () => setCollapsedTypes((prev) => {
                    const next = new Set(prev);
                    if (next.has(typ)) next.delete(typ); else next.add(typ);
                    return next;
                  });
                  return (
                    <div key={typ}>
                      <button style={S.typeGroupLabelBtn} onClick={toggleCollapse}>
                        <span style={{ width: 10, display: 'inline-block' }}>{collapsed ? '▸' : '▾'}</span>
                        {typeLabel[typ]}
                      </button>
                      {!collapsed && group.map((c) => {
                        const on = c.projectId === active?.projectId;
                        const who = c.people[0]?.name ?? '担当AI';
                        return (
                          <button
                            key={c.projectId}
                            draggable
                            onDragStart={() => setDragId(c.projectId)}
                            onDragEnd={() => setDragId(null)}
                            onDragOver={(e) => { if (dragId != null) e.preventDefault(); }}
                            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDropOn(c.projectId, false); }}
                            onClick={() => { setActiveId(c.projectId); if (isMobile) setMobileLevel(2); }}
                            style={{ ...S.chan, ...(on ? S.chanActive : null), ...(dragId === c.projectId ? S.chanDragging : null) }}
                          >
                            <span style={S.dragHandle} aria-hidden>⠿</span>
                            <span style={{ ...S.chanText, flex: 1 }}>
                              <span style={S.chanName}>{c.name}</span>
                              <span style={S.chanWho}>担当: {who}{c.people.length > 1 ? ` 他${c.people.length - 1}人` : ''}</span>
                            </span>
                            <Badge n={unreadByProject.get(c.projectId) ?? 0} />
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* アーカイブ区画（折りたたみ）：見出しクリックで開閉。折りたたみ中もドロップで退避は可能。 */}
          <div
            style={S.archiveZone}
            onDragOver={(e) => { if (dragId != null) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); onDropOn(null, true); }}
          >
            <button style={S.archiveHeadBtn} onClick={() => setShowProjectArchive((v) => !v)}>
              <span style={{ width: 12, display: 'inline-block', color: '#b9a3b9' }}>{showProjectArchive ? '▾' : '▸'}</span>
              <span style={S.archiveLabel}>
                アーカイブ{list.filter((c) => c.archived).length > 0 ? `（${list.filter((c) => c.archived).length}）` : ''}
              </span>
              <span style={S.archiveLine} />
            </button>
            {showProjectArchive && (list.filter((c) => c.archived).length === 0 ? (
              <div style={S.archiveHint}>ここにドラッグで一旦おいておけます</div>
            ) : (
              list.filter((c) => c.archived).map((c) => {
                const on = c.projectId === active?.projectId;
                const who = c.people[0]?.name ?? '担当AI';
                return (
                  <button
                    key={c.projectId}
                    draggable
                    onDragStart={() => setDragId(c.projectId)}
                    onDragEnd={() => setDragId(null)}
                    onDragOver={(e) => { if (dragId != null) e.preventDefault(); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDropOn(c.projectId, true); }}
                    onClick={() => { setActiveId(c.projectId); if (isMobile) setMobileLevel(2); }}
                    style={{ ...S.chan, ...S.chanArchived, ...(on ? S.chanActive : null), ...(dragId === c.projectId ? S.chanDragging : null) }}
                  >
                    <span style={S.dragHandle} aria-hidden>⠿</span>
                    <span style={{ ...S.chanText, flex: 1 }}>
                      <span style={S.chanName}>{c.name}</span>
                      <span style={S.chanWho}>担当: {who}{c.people.length > 1 ? ` 他${c.people.length - 1}人` : ''}</span>
                    </span>
                    <Badge n={unreadByProject.get(c.projectId) ?? 0} />
                  </button>
                );
              })
            ))}
          </div>
        </div>
        <ClaudeUsageWidget />
      </aside>

      {/* プロジェクト↔チーム リサイザ */}
      {!isMobile && <Resizer onStart={(e) => startResize(e, sideWidth, SIDE_MIN, SIDE_MAX, setSideWidth)} />}

      {/* 2) チーム */}
      <div style={isMobile ? (mobileLevel !== 2 ? { display: 'none' } : { ...S.teamCol, width: '100%' }) : { ...S.teamCol, width: teamWidth }}>
        {isMobile && (
          <div style={S.mobileBackBar}>
            <button style={S.mobileBackBtn} onClick={() => setMobileLevel(1)}>‹ チャット</button>
            <span style={S.mobileBackTitle}>{active?.name ?? ''}</span>
          </div>
        )}
        {!active ? (
          <div style={S.threadsEmpty}>プロジェクトを選んでください</div>
        ) : globalBC ? (
          // 全プロジェクト・ブロードキャストはチーム概念が無い。送信先サマリだけ出す。
          <>
            <div style={S.threadsHead}>
              <span style={{ fontWeight: 700, color: '#1d1c1d' }}>📢 全プロジェクト</span>
            </div>
            <div style={{ ...S.threadList, padding: 14 }}>
              <div style={{ fontSize: 13, color: '#1d1c1d', fontWeight: 700, marginBottom: 6 }}>
                送信先
              </div>
              <div style={{ fontSize: 13, color: '#616061', lineHeight: 1.7 }}>
                {list.length} プロジェクト / {active.teams.length} チーム
                <br />
                （オンラインDIRを持つチームにだけ実送信されます）
              </div>
              <div style={{ marginTop: 12, fontSize: 12, color: '#9b9b9b', lineHeight: 1.7 }}>
                右の入力欄から一斉送信。各チームに新しいスレッドが立ち、返信はそのスレッドで行われます。
              </div>
            </div>
          </>
        ) : globalFav ? (
          // 全体お気に入りはチーム概念が無い。件数サマリだけ出す（一覧は右のスレッド列）。
          <>
            <div style={S.threadsHead}>
              <span style={{ fontWeight: 700, color: '#1d1c1d' }}>★ お気に入り</span>
            </div>
            <div style={{ ...S.threadList, padding: 14 }}>
              <div style={{ fontSize: 13, color: '#1d1c1d', fontWeight: 700, marginBottom: 6 }}>
                全プロジェクトのお気に入り
              </div>
              <div style={{ fontSize: 13, color: '#616061', lineHeight: 1.7 }}>
                {allFavoriteThreads.length} 件
              </div>
              <div style={{ marginTop: 12, fontSize: 12, color: '#9b9b9b', lineHeight: 1.7 }}>
                右の一覧から開くと、該当プロジェクトのチーム・スレッドへ移動します。
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={S.threadsHead}>
              <span style={{ fontWeight: 700, color: '#1d1c1d' }}>チーム</span>
            </div>
            <div style={S.threadList}>
              {/* お気に入り仮想チーム（一番上・固定）。DBには作らない機能上のチーム。 */}
              {favoriteThreads.length > 0 && (
                <button
                  style={{
                    ...S.threadItem,
                    ...(activeTeamId === FAVORITES ? S.threadActive : null),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                  onClick={() => { pickTeam(FAVORITES); if (isMobile) setMobileLevel(3); }}
                  title="お気に入りに登録したスレッドの一覧"
                >
                  <span style={{ fontSize: 14, color: '#f59e0b' }}>★</span>
                  <div style={{ ...S.threadTitle, flex: 1, fontWeight: 700 }}>お気に入り</div>
                  <Badge n={favoriteThreads.reduce((s, t) => s + t.unread, 0)} />
                </button>
              )}
              {/* ブロードキャスト仮想チーム（固定）。DBには作らない機能上のチーム。 */}
              <button
                style={{
                  ...S.threadItem,
                  ...(activeTeamId === BROADCAST ? S.threadActive : null),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
                onClick={() => { pickTeam(BROADCAST); if (isMobile) setMobileLevel(3); }}
                title="このプロジェクト内の全チームへ、新しいスレッドで一斉発信します"
              >
                <span style={{ ...S.teamDot, background: '#7c3aed' }} />
                <div style={{ ...S.threadTitle, flex: 1, fontWeight: 700 }}>📢 ブロードキャスト</div>
                <Badge n={broadcastUnread} />
              </button>
              {active.teams.length === 0 && (
                <div style={S.teamEmpty}>チーム未アサイン（設定で DIR にチームを割り当て）</div>
              )}
              {active.teams.map((t) => {
                // チームの死活＝そのチームに割り当てた DIR(デバイス)が1つでもオンラインなら通じている(緑)、
                // 全部オフラインなら通じていない(赤)。Claude の応答可否はこの DIR の有無/オンラインで判断。
                const teamOnline = active.pcs.some((p) => p.teamIds.includes(t.id) && p.online);
                // このチームの DIR のうち、トンネル名が登録済みの端末を1つ選んで VS Code へのリンクを出す。
                // 同じチームに複数 DIR がぶら下がることがあるのでオンラインを優先する。
                // 未登録(tunnelHost=null)しか無いチームはリンクを出さない。
                const tunnelPcs = active.pcs.filter((p) => p.teamIds.includes(t.id) && p.tunnelHost);
                const tunnelPc = tunnelPcs.find((p) => p.online) ?? tunnelPcs[0];
                const tunnelUrl = tunnelPc
                  ? `https://vscode.dev/tunnel/${tunnelPc.tunnelHost}${tunnelPc.cwd}?vscode-lang=ja`
                  : null;
                return (
                  <button
                    key={t.id}
                    style={{
                      ...S.threadItem,
                      ...(activeTeamId === t.id ? S.threadActive : null),
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                    onClick={() => { pickTeam(t.id); if (isMobile) setMobileLevel(3); }}
                  >
                    <span
                      style={{ ...S.teamDot, background: teamOnline ? '#2bac76' : '#e01e5a' }}
                      title={teamOnline ? '通じています（DIRオンライン）' : '通じていません（全DIRオフライン）'}
                    />
                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <div style={S.threadTitle}>{teamLabel(t)}</div>
                      {tunnelPc && (
                        <div style={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>
                          {tunnelPc.deviceName}
                        </div>
                      )}
                    </div>
                    {tunnelUrl && (
                      // eslint-disable-next-line jsx-a11y/anchor-is-valid
                      <a
                        href={tunnelUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={S.tunnelLink}
                        title={`VS Code で開く — ${tunnelPc!.deviceName}: ${tunnelPc!.cwd}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                          fill="#007ACC"
                        >
                          <path d="M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L3.899 12 .326 15.26a1 1 0 0 0 .001 1.479L1.65 17.94a.999.999 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 19.983V4.017a1.5 1.5 0 0 0-.85-1.43zm-5.146 14.861L10.826 12l7.178-5.448v10.896z"/>
                        </svg>
                      </a>
                    )}
                    <Badge n={unreadByTeam.get(t.id) ?? 0} />
                  </button>
                );
              })}
              {/* 未分類：有効チームに属さない（旧チーム/未割当）会話の置き場。あるときだけ出す。 */}
              {hasUnclassified && (
                <button
                  style={{
                    ...S.threadItem,
                    ...(activeTeamId === UNCLASSIFIED ? S.threadActive : null),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                  onClick={() => { pickTeam(UNCLASSIFIED); if (isMobile) setMobileLevel(3); }}
                  title="チーム未割当・旧チームの会話"
                >
                  <span style={{ ...S.teamDot, background: '#9b9b9b' }} />
                  <div style={{ ...S.threadTitle, flex: 1, color: '#6b7280' }}>未分類</div>
                  <Badge n={unreadByTeam.get(UNCLASSIFIED) ?? 0} />
                </button>
              )}
              {/* チーム群とは水平線で分ける。会話ではなくプロジェクト（こねっこ）が
                  持つ情報＝メモ／タスク。選ぶと右側にそれぞれの一覧を出す。 */}
              <div style={S.notesSep} />
              <button
                style={{
                  ...S.threadItem,
                  ...(activeView === 'memos' ? S.threadActive : null),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
                onClick={() => { setActiveView('memos'); if (isMobile) setMobileLevel(3); }}
                title="このプロジェクトのメモ一覧（会話ではなく上書きで管理）"
              >
                <span style={{ fontSize: 14 }}>📝</span>
                <div style={{ ...S.threadTitle, flex: 1, fontWeight: 700 }}>メモ</div>
              </button>
              <div style={S.notesSep} />
              <button
                style={{
                  ...S.threadItem,
                  ...(activeView === 'tasks' ? S.threadActive : null),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
                onClick={() => { setActiveView('tasks'); if (isMobile) setMobileLevel(3); }}
                title="このプロジェクトのタスク一覧（やること管理）"
              >
                <span style={{ fontSize: 14 }}>🗒</span>
                <div style={{ ...S.threadTitle, flex: 1, fontWeight: 700 }}>タスク</div>
              </button>
            </div>
          </>
        )}
      </div>

      {/* チーム↔スレッド リサイザ */}
      {!isMobile && <Resizer onStart={(e) => startResize(e, teamWidth, TEAM_MIN, TEAM_MAX, setTeamWidth)} />}

      {/* メモ／タスク選択時は ③スレッド＋④会話 を NotesPanel（一覧＋編集）に差し替える。 */}
      {active && activeView !== 'threads' ? (
        <NotesPanel
          key={`${activeView}:${active.projectId}`}
          projectId={active.projectId}
          kind={activeView}
          listWidth={threadsWidth}
          isMobile={isMobile}
          mobileLevel={mobileLevel}
          setMobileLevel={setMobileLevel}
        />
      ) : (
      <>
      {/* 3) スレッド */}
      <div style={isMobile ? (mobileLevel !== 3 ? { display: 'none' } : { ...S.threads, width: '100%' }) : { ...S.threads, width: threadsWidth }}>
        {isMobile && (
          <div style={S.mobileBackBar}>
            <button style={S.mobileBackBtn} onClick={() => setMobileLevel(2)}>‹ チーム</button>
            <span style={S.mobileBackTitle}>{active?.teams.find(t => t.id === activeTeamId)?.name ?? (activeTeamId === BROADCAST ? 'ブロードキャスト' : activeTeamId === UNCLASSIFIED ? '未分類' : activeTeamId === FAVORITES ? 'お気に入り' : '')}</span>
          </div>
        )}
        {!active ? (
          <div style={S.threadsEmpty}>プロジェクトを選んでください</div>
        ) : activeTeamId === FAVORITES ? (
          // お気に入り一覧。通常＝選択中PJ内をチームをまたいで表示（チーム名のみ）。
          // 全体お気に入り＝全PJ横断で表示（PJ名＋チーム名を出し、開くと該当PJ/チーム/スレッドへ移動）。
          <>
            <div style={S.threadsHead}>
              <span style={{ fontWeight: 700, color: '#1d1c1d' }}>★ お気に入り</span>
            </div>
            <div style={S.threadList}>
              {(globalFav ? allFavoriteThreads : favoriteThreads).length === 0 ? (
                <div style={S.teamEmpty}>お気に入りに登録したスレッドはありません</div>
              ) : (
                (globalFav ? allFavoriteThreads : favoriteThreads).map((t) => {
                  const g = globalFav ? (t as GlobalFavThread) : null;
                  const teamName = g
                    ? g.teamName
                    : active.teams.find((tm) => tm.id === t.teamId)?.name ?? '未分類';
                  return (
                    <button
                      key={t.conversationId}
                      style={{
                        ...S.threadItem,
                        ...(activeSession === t.conversationId ? S.threadActive : null),
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                      onClick={() => {
                        // 全体お気に入りは PJ をまたぐので、該当PJ/チームへ切り替えてスレッドを開く。
                        if (g) { openBroadcastChild(g.projectId, g.jumpTeamId, t.conversationId); return; }
                        pickThread(t.conversationId); if (isMobile) setMobileLevel(4);
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={S.threadTitle}>{t.title || '(無題)'}</div>
                        <div style={S.threadSub}>{g ? `${g.projectName} / ${teamName}` : teamName} · {t.count}件 · {fmt(t.lastAt)}</div>
                      </div>
                      <Badge n={t.unread} />
                      <span
                        role="button"
                        title="お気に入りを解除"
                        style={{ ...S.threadActBtn, color: '#f59e0b' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          favoriteMut.mutate({ conversationId: t.conversationId, favorited: false });
                        }}
                      >
                        ★
                      </span>
                      <span
                        role="button"
                        title="完了としてクローズ（あとで復活できます）"
                        style={S.threadActBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          completeMut.mutate({ conversationId: t.conversationId, completed: true });
                          favoriteMut.mutate({ conversationId: t.conversationId, favorited: false });
                        }}
                      >
                        ✅
                      </span>
                      <span
                        role="button"
                        title="中止としてクローズ（あとで復活できます）"
                        style={S.threadActBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          cancelMut.mutate({ conversationId: t.conversationId, cancelled: true });
                          favoriteMut.mutate({ conversationId: t.conversationId, favorited: false });
                        }}
                      >
                        🚫
                      </span>
                      <span
                        role="button"
                        title="アーカイブ（あとで復活できます）"
                        style={S.threadActBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          archiveMut.mutate({ conversationId: t.conversationId, archived: true });
                          favoriteMut.mutate({ conversationId: t.conversationId, favorited: false });
                        }}
                      >
                        📥
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </>
        ) : activeTeamId === BROADCAST ? (
          // ブロードキャスト束ね一覧。1グループ=1回の一斉送信。展開で配下の実体スレ（チームごと）を見せる。
          <>
            <div style={S.threadsHead}>
              <span style={{ fontWeight: 700, color: '#1d1c1d' }}>ブロードキャスト</span>
              <button style={S.newBtn} onClick={() => { pickThread(null); if (isMobile) setMobileLevel(4); }}>＋ 新規一斉送信</button>
            </div>
            <div style={S.threadList}>
              <button
                style={{ ...S.threadItem, ...(activeSession === null ? S.threadActive : null) }}
                onClick={() => { pickThread(null); if (isMobile) setMobileLevel(4); }}
              >
                <div style={S.threadTitle}>📢 新しい一斉送信</div>
                <div style={S.threadSub}>
                  {globalBC
                    ? '全プロジェクトの全チームへ新規スレッドで送る'
                    : 'このプロジェクト内の全チームへ新規スレッドで送る'}
                </div>
              </button>
              {broadcasts.length === 0 && (
                <div style={S.teamEmpty}>まだ一斉送信はありません</div>
              )}
              {broadcasts.map((b) => {
                const open = expandedBroadcast === b.broadcastId;
                return (
                  <Fragment key={b.broadcastId}>
                    <button
                      style={{ ...S.threadItem, display: 'flex', alignItems: 'center', gap: 8 }}
                      onClick={() =>
                        setExpandedBroadcast(open ? null : b.broadcastId)
                      }
                      title="クリックで配下スレッド（チームごと）を開閉"
                    >
                      <span style={{ color: '#7c3aed' }}>{open ? '▾' : '▸'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={S.threadTitle}>{b.text || '(無題)'}</div>
                        <div style={S.threadSub}>{b.teamCount}チームへ送信 · {fmt(b.lastAt)}</div>
                      </div>
                      <Badge n={b.unread} />
                    </button>
                    {open &&
                      b.threads.map((ch) => (
                        <button
                          key={ch.conversationId}
                          style={{
                            ...S.threadItem,
                            paddingLeft: 26,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                          onClick={() => openBroadcastChild(ch.projectId, ch.teamId, ch.conversationId)}
                          title="このチームの実体スレッドを開く（返信はこちらで完結）"
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={S.threadTitle}>
                              {ch.teamIcon ? `${ch.teamIcon} ` : ''}{ch.teamName}
                            </div>
                            <div style={S.threadSub}>
                              {globalBC ? `${ch.projectName} · ` : ''}{ch.count}件 · {fmt(ch.lastAt)}
                            </div>
                          </div>
                          <Badge n={ch.unread} />
                        </button>
                      ))}
                  </Fragment>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <div style={S.threadsHead}>
              <span style={{ fontWeight: 700, color: '#1d1c1d' }}>スレッド</span>
              <button style={S.newBtn} onClick={() => { pickThread(null); if (isMobile) setMobileLevel(4); }}>＋ 新規</button>
            </div>
            <div style={S.threadList}>
              <button
                style={{ ...S.threadItem, ...(activeSession === null ? S.threadActive : null) }}
                onClick={() => { pickThread(null); if (isMobile) setMobileLevel(4); }}
              >
                <div style={S.threadTitle}>＋ 新しいスレッド</div>
                <div style={S.threadSub}>新しい会話を始める</div>
              </button>
              {shownThreads.length === 0 && (
                <div style={S.teamEmpty}>このチームのスレッドはまだありません</div>
              )}
              {shownThreads.map((t) => (
                <button
                  key={t.conversationId}
                  style={{
                    ...S.threadItem,
                    ...(activeSession === t.conversationId ? S.threadActive : null),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                  onClick={() => { pickThread(t.conversationId); if (isMobile) setMobileLevel(4); }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {editingTitle?.conversationId === t.conversationId ? (
                      <input
                        autoFocus
                        value={editingTitle.value}
                        style={S.titleInput}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setEditingTitle({ conversationId: t.conversationId, value: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitTitleEdit(); if (e.key === 'Escape') setEditingTitle(null); }}
                        onBlur={commitTitleEdit}
                      />
                    ) : (
                      <div
                        style={S.threadTitle}
                        onDoubleClick={(e) => { e.stopPropagation(); setEditingTitle({ conversationId: t.conversationId, value: t.title || '' }); }}
                        title="ダブルクリックでタイトルを編集"
                      >
                        {t.title || '(無題)'}
                      </div>
                    )}
                    <div style={S.threadSub}>{t.count}件 · {fmt(t.lastAt)}</div>
                  </div>
                  <Badge n={t.unread} />
                  <span
                    role="button"
                    title={t.favorited ? 'お気に入りを解除' : 'お気に入りに追加'}
                    style={{ ...S.threadActBtn, color: t.favorited ? '#f59e0b' : '#9b9b9b' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      favoriteMut.mutate({ conversationId: t.conversationId, favorited: !t.favorited });
                    }}
                  >
                    {t.favorited ? '★' : '☆'}
                  </span>
                  <span
                    role="button"
                    title="完了としてクローズ（あとで復活できます）"
                    style={S.threadActBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      completeMut.mutate({ conversationId: t.conversationId, completed: true });
                      if (t.favorited) favoriteMut.mutate({ conversationId: t.conversationId, favorited: false });
                    }}
                  >
                    ✅
                  </span>
                  <span
                    role="button"
                    title="中止としてクローズ（あとで復活できます）"
                    style={S.threadActBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelMut.mutate({ conversationId: t.conversationId, cancelled: true });
                      if (t.favorited) favoriteMut.mutate({ conversationId: t.conversationId, favorited: false });
                    }}
                  >
                    🚫
                  </span>
                  <span
                    role="button"
                    title="アーカイブ（あとで復活できます）"
                    style={S.threadActBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      archiveMut.mutate({ conversationId: t.conversationId, archived: true });
                      if (t.favorited) favoriteMut.mutate({ conversationId: t.conversationId, favorited: false });
                    }}
                  >
                    📥
                  </span>
                </button>
              ))}

              {/* アーカイブ欄（折りたたみ）。復活ボタンで通常一覧へ戻す。 */}
              {archivedThreads.length > 0 && (
                <>
                  <button
                    style={{ ...S.threadItem, display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280' }}
                    onClick={() => setShowArchived((v) => !v)}
                  >
                    <span style={{ color: '#9b9b9b' }}>{showArchived ? '▾' : '▸'}</span>
                    <div style={{ ...S.threadTitle, flex: 1, color: '#6b7280' }}>
                      アーカイブ（{archivedThreads.length}）
                    </div>
                  </button>
                  {showArchived &&
                    archivedThreads.map((t) => (
                      <button
                        key={t.conversationId}
                        style={{
                          ...S.threadItem,
                          ...(activeSession === t.conversationId ? S.threadActive : null),
                          paddingLeft: 26,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          opacity: 0.75,
                        }}
                        onClick={() => { pickThread(t.conversationId); if (isMobile) setMobileLevel(4); }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={S.threadTitle}>{t.title || '(無題)'}</div>
                          <div style={S.threadSub}>{t.count}件 · {fmt(t.lastAt)}</div>
                        </div>
                        <span
                          role="button"
                          title="復活して通常一覧へ戻す"
                          style={S.threadActBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            archiveMut.mutate({ conversationId: t.conversationId, archived: false });
                          }}
                        >
                          ↩
                        </span>
                        <span
                          role="button"
                          title="完全に削除する（元に戻せません）"
                          style={{ ...S.threadActBtn, color: '#e01e5a' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMut.mutate(t.conversationId);
                          }}
                        >
                          🗑
                        </span>
                      </button>
                    ))}
                </>
              )}

              {/* 完了セクション */}
              {completedThreads.length > 0 && (
                <>
                  <button
                    style={{ ...S.threadItem, display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280' }}
                    onClick={() => setShowCompleted((v) => !v)}
                  >
                    <span style={{ color: '#9b9b9b' }}>{showCompleted ? '▾' : '▸'}</span>
                    <div style={{ ...S.threadTitle, flex: 1, color: '#6b7280' }}>
                      完了（{completedThreads.length}）
                    </div>
                  </button>
                  {showCompleted &&
                    completedThreads.map((t) => (
                      <button
                        key={t.conversationId}
                        style={{
                          ...S.threadItem,
                          ...(activeSession === t.conversationId ? S.threadActive : null),
                          paddingLeft: 26,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          opacity: 0.75,
                        }}
                        onClick={() => { pickThread(t.conversationId); if (isMobile) setMobileLevel(4); }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={S.threadTitle}>{t.title || '(無題)'}</div>
                          <div style={S.threadSub}>{t.count}件 · {fmt(t.lastAt)}</div>
                        </div>
                        <span
                          role="button"
                          title="通常一覧へ戻す"
                          style={S.threadActBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            completeMut.mutate({ conversationId: t.conversationId, completed: false });
                          }}
                        >
                          ↩
                        </span>
                        <span
                          role="button"
                          title="完全に削除する（元に戻せません）"
                          style={{ ...S.threadActBtn, color: '#e01e5a' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMut.mutate(t.conversationId);
                          }}
                        >
                          🗑
                        </span>
                      </button>
                    ))}
                </>
              )}

              {/* 中止セクション */}
              {cancelledThreads.length > 0 && (
                <>
                  <button
                    style={{ ...S.threadItem, display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280' }}
                    onClick={() => setShowCancelled((v) => !v)}
                  >
                    <span style={{ color: '#9b9b9b' }}>{showCancelled ? '▾' : '▸'}</span>
                    <div style={{ ...S.threadTitle, flex: 1, color: '#6b7280' }}>
                      中止（{cancelledThreads.length}）
                    </div>
                  </button>
                  {showCancelled &&
                    cancelledThreads.map((t) => (
                      <button
                        key={t.conversationId}
                        style={{
                          ...S.threadItem,
                          ...(activeSession === t.conversationId ? S.threadActive : null),
                          paddingLeft: 26,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          opacity: 0.75,
                        }}
                        onClick={() => { pickThread(t.conversationId); if (isMobile) setMobileLevel(4); }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={S.threadTitle}>{t.title || '(無題)'}</div>
                          <div style={S.threadSub}>{t.count}件 · {fmt(t.lastAt)}</div>
                        </div>
                        <span
                          role="button"
                          title="通常一覧へ戻す"
                          style={S.threadActBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelMut.mutate({ conversationId: t.conversationId, cancelled: false });
                          }}
                        >
                          ↩
                        </span>
                        <span
                          role="button"
                          title="完全に削除する（元に戻せません）"
                          style={{ ...S.threadActBtn, color: '#e01e5a' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMut.mutate(t.conversationId);
                          }}
                        >
                          🗑
                        </span>
                      </button>
                    ))}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* スレッド↔会話 リサイザ */}
      {!isMobile && <Resizer onStart={(e) => startResize(e, threadsWidth, THREADS_MIN, THREADS_MAX, setThreadsWidth)} />}

      {/* 4) 会話 */}
      <section style={isMobile && mobileLevel !== 4 ? { display: 'none' } : S.main}>
        {!active ? (
          <div style={S.empty}>左でプロジェクトを選んでください。</div>
        ) : (
          <>
            {isMobile && (
              <div style={S.mobileBackBar}>
                <button style={S.mobileBackBtn} onClick={() => setMobileLevel(3)}>‹ スレッド</button>
                <span style={S.mobileBackTitle}>{active.name}</span>
              </div>
            )}
            <header style={isMobile ? { display: 'none' } : S.head}>
              <div style={{ minWidth: 0 }}>
                {/* タイトルはプロジェクト名のみ（チーム名は上部パンくずで分かるので出さない）。 */}
                <div style={S.headTitle}>#{active.name}</div>
                {/* 「担当AIが応答」の行は廃止。代わりに選択中チームのメンバー一覧を出す。 */}
                <div style={S.headMembers}>
                  {teamMembers.length > 0 ? (
                    teamMembers.map((m) => (
                      <span key={m.id} style={S.member} title={m.isLeader ? `${m.name}（PM）` : m.name}>
                        <span style={S.memberAv}>{m.icon || m.name.charAt(0)}</span>
                        <span style={S.memberName}>{m.name}</span>
                        {m.isLeader && <span style={S.memberPm}>👑</span>}
                      </span>
                    ))
                  ) : (
                    <span style={S.headTopic}>
                      {activeTeamId === UNCLASSIFIED ? 'チーム未割当の会話' : 'メンバーがいません'}
                    </span>
                  )}
                </div>
              </div>
              {/* 右肩: 📞通話（AI-WF265/要望#213）＋ 🔔完了push通知トグル（AI-WF262/要望#211）＋ 表示モード切替。モバイルでは非表示。 */}
              <div style={S.headRight} className="k-mobile-hidden">
              <CallButton
                conversationId={activeSession}
                pcOnline={
                  activeTeamId != null && activeTeamId !== UNCLASSIFIED && activeTeamId !== BROADCAST && activeTeamId !== FAVORITES
                    ? active.pcs.some((p) => p.teamIds.includes(activeTeamId) && p.online)
                    : active.pcs.some((p) => p.online)
                }
              />
              <PushToggle />
              {/* チャット表示モード切替（LINE風 / Slack風）。localStorage 保存。 */}
              <div style={S.modeToggle}>
                <button
                  style={{ ...S.modeBtn, ...(chatMode === 'line' ? S.modeBtnOn : null) }}
                  onClick={() => setChatMode('line')}
                  title="LINE風（右=自分 / 左=相手の吹き出し）"
                >
                  LINE
                </button>
                <button
                  style={{ ...S.modeBtn, ...(chatMode === 'slack' ? S.modeBtnOn : null) }}
                  onClick={() => setChatMode('slack')}
                  title="Slack風（左寄せ・アイコン＋名前）"
                >
                  Slack
                </button>
              </div>
              </div>
            </header>

            <div
              ref={logRef}
              style={S.log}
              onScroll={(e) => {
                if (scrollLockRef.current || !activeSession) return;
                const el = e.currentTarget;
                const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
                // 新着が来る「前」に下端付近に居たかを記録（新着追従の判定に使う）。
                atBottomRef.current = isAtBottom;
                saveScrollPos(activeSession, el.scrollTop);
                if (isAtBottom) { setShowJump(false); setJumpCount(0); }
                else { setShowJump(true); }
              }}
            >
              {shown.length === 0 && activeTeamId === BROADCAST && (
                <div style={S.logEmpty}>
                  <div style={{ ...S.logHash, background: '#7c3aed' }}>📢</div>
                  <div style={{ fontWeight: 700, fontSize: 18, color: '#1d1c1d' }}>
                    {globalBC ? '全プロジェクト・ブロードキャスト' : 'ブロードキャスト'}
                  </div>
                  <div style={{ color: '#616061', marginTop: 4 }}>
                    {globalBC
                      ? `全プロジェクト（${list.length}PJ / ${active.teams.length}チーム）へ、それぞれ新しいスレッドで一斉発信します。`
                      : `#${active.name} 内の全チーム（${active.teams.length}チーム）へ、それぞれ新しいスレッドで一斉発信します。`}
                    返信は各チームのスレッドで行われ、左の一覧に束ねて表示されます。
                  </div>
                </div>
              )}
              {shown.length === 0 && activeTeamId !== BROADCAST && (
                <div style={S.logEmpty}>
                  <div style={S.logHash}>{personName.charAt(0)}</div>
                  <div style={{ fontWeight: 700, fontSize: 18, color: '#1d1c1d' }}>{personName}</div>
                  <div style={{ color: '#616061', marginTop: 4 }}>
                    #{active.name} の担当。話しかけて始めましょう（会話は記憶されます）。
                  </div>
                </div>
              )}
              {shown.map((m, i) => {
                const isUser = m.role === 'user';
                const isAi = m.role === 'ai';
                // 発言者は fromRef から解決（AI=実話者：PM/コントローラ/メンション先）。
                const who = resolveSender(m.role, m.fromRef);
                const sender = who.name;
                const av = isUser
                  ? { bg: '#2563eb', ch: '私' }
                  : isAi
                    ? { bg: '#4a154b', ch: who.icon || who.name.charAt(0) }
                    : { bg: '#b45309', ch: '!' };
                // AI-WF251 (C1/C2): streaming 中は逐次表示＋クルクル。final で停止。
                const streaming = isAi && m.streamState === 'streaming';
                // 本文（AI は Markdown、その他はプレーン）。両モード共通。
                const body = isAi ? (
                  <AiMessageBody text={m.text} streaming={streaming} />
                ) : (
                  <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</span>
                );

                // 添付画像（チャット欄表示）。クリックで拡大ダイアログ。
                const images = m.atts && m.atts.length ? (
                  <div style={S.imgRow}>
                    {m.atts.map((a) => (
                      <ChatImage key={a.key} att={a} onOpen={(url, name) => setDialog({ url, name })} />
                    ))}
                  </div>
                ) : null;

                // 会話状態（対応中/返信待ち）。AI-WF251 (C4): 入力待ち(stopReason=input_waiting)は
                // final でも「返信待ち」を出す（再ログイン後も永続 stopReason から復元）。
                // outcome 値(ok/error/timeout/cancelled)ではバッジを出さない（＝完了メッセージに付けない）。
                // input_waiting（返信待ち）は無効化。誤検知するヒューリスティックのため別要望に切り出し。
                // テキストでは status に関係なく会話は繋がる（飾りのため今回は出さない）。
                const waiting = false;
                const working = m.status === '対応中';
                const statusBadge = (waiting || working) && !streaming ? (
                  <span
                    style={{ ...S.statusBadge, ...(waiting ? S.statusWaiting : S.statusWorking) }}
                  >
                    {waiting ? '💬 返信待ち' : '⏳ 対応中'}
                  </span>
                ) : null;
                // AI-WF262 (要望#211/TA-5): 非同期の種別チップ（受領/作業中/完了）。
                // normal/undefined は表示なし＝従来見た目不変。progress は受領メッセージが
                // in-place 遷移してくる（#204 仕様・1メッセージ・低id固定）。final は streaming
                // 終了後だけ出す（クルクル中に「完了」を出さない）。
                const kindChip =
                  m.msgKind === 'ack' ? (
                    <span style={{ ...S.statusBadge, ...S.kindAck }} data-testid="chip-ack">✅ 受領</span>
                  ) : m.msgKind === 'progress' ? (
                    <span style={{ ...S.statusBadge, ...S.kindProgress }} data-testid="chip-progress">🔄 作業中</span>
                  ) : m.msgKind === 'final' && m.streamState === 'final' ? (
                    <span style={{ ...S.statusBadge, ...S.kindFinal }} data-testid="chip-final">📬 完了</span>
                  ) : null;
                const actionsBlock = m.actions && m.actions.length ? (
                  <div style={S.actionList}>
                    {m.actions.map((a, ai) => (
                      <div key={ai} style={S.actionItem}>
                        <div style={S.actionHead}>
                          <span style={{ ...S.actionStatusDot, background: ACTION_STATUS_COLOR[a.status] }} />
                          <span style={S.actionName}>{actionKindLabel(a.kind)} {a.name}</span>
                          <span style={{ ...S.actionStatusText, color: ACTION_STATUS_COLOR[a.status] }}>
                            {ACTION_STATUS_LABEL[a.status]}
                          </span>
                        </div>
                        {a.output && <pre style={S.actionOutput}>{a.output}</pre>}
                      </div>
                    ))}
                  </div>
                ) : null;
                // 「動いてる」鼓動ログ。AI の作業内容を時刻つきで追記表示する（debug 用）。
                // 進行中は点滅ドット付き、final 後もログは残す（どこで止まったか辿れる）。
                // 開閉・経過時間表示は LiveActivityLog コンポーネントが担う。
                const liveLine = isAi && m.liveActivity && m.liveActivity.length ? (
                  <LiveActivityLog entries={m.liveActivity} streamState={m.streamState} />
                ) : null;
                // メッセージ日時（サーバ時刻・秒精度）。送信/返信のタイミング切り分け用。
                const tsLine = m.createdAt ? (
                  <div style={S.msgTime}>{fmtSec(m.createdAt)}</div>
                ) : null;
                const extras = (statusBadge || kindChip || actionsBlock || liveLine) ? (
                  <>
                    {statusBadge}
                    {kindChip}
                    {liveLine}
                    {actionsBlock}
                  </>
                ) : null;

                // タスク#408→#413: run 識別子を各メッセージ下に sessionId= / requestId= の2行で表示し、
                // それぞれコピーアイコンで単独コピーできる（ハイフン連結は読みにくかったため分離）。
                // sessionId 未確定（新スレッド初回=空文字）の行・run 外の行は該当行を出さない。
                // タスク#408: 実行中(streaming)の AI メッセージは個別に停止できる（最新以外も）。
                const cancellable =
                  isAi && m.streamState === 'streaming' && !!m.requestId && m.deviceId != null;
                // タスク#413: AI は「表示/ソース」タブ横に本文コピーがあるので、タブの無い自分
                // （ユーザー）のメッセージにはここに本文コピーを置く。
                const userCopy = isUser && m.text ? (
                  <div style={S.runIdRow}>
                    <CopyBtn value={m.text} title="メッセージをコピー" />
                  </div>
                ) : null;
                const idLine = (userCopy || m.sessionId || m.requestId) ? (
                  <div style={S.runIdBlock}>
                    {userCopy}
                    {m.sessionId ? (
                      <div style={S.runIdRow}>
                        <span style={S.runIdText}>sessionId={m.sessionId}</span>
                        <CopyBtn value={m.sessionId} title="sessionId をコピー" />
                      </div>
                    ) : null}
                    {m.requestId ? (
                      <div style={S.runIdRow}>
                        <span style={S.runIdText}>requestId={m.requestId}</span>
                        <CopyBtn value={m.requestId} title="requestId をコピー" />
                        {cancellable && (
                          <button
                            style={S.msgStopBtn}
                            onClick={() => void cancelRun(m.deviceId!, m.requestId!)}
                          >
                            ⏹ この実行を停止
                          </button>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null;

                // 「ここから未読」区切り（開いた時点の最初の未読の手前に一度だけ出す）。
                const divider = unreadAnchorIdx === i && i < serverMsgs.length ? (
                  <div ref={unreadDividerRef} style={S.unreadDivider}>
                    <span style={S.unreadDividerLine} />
                    <span style={S.unreadDividerLabel}>ここから未読</span>
                    <span style={S.unreadDividerLine} />
                  </div>
                ) : null;

                if (chatMode === 'slack') {
                  // Slack風: 全員左寄せ・アイコン＋名前＋本文。
                  return (
                    <Fragment key={i}>
                      {divider}
                      <div style={S.msg}>
                        <div style={{ ...S.avatar, background: av.bg }}>{av.ch}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 800, color: '#1d1c1d', fontSize: 15 }}>{sender}</div>
                          <div style={{ ...S.msgText, ...(m.role === 'system' ? S.msgSystem : null) }}>{body}</div>
                          {images}
                          {extras}
                          {tsLine}
                          {idLine}
                        </div>
                      </div>
                    </Fragment>
                  );
                }

                // LINE風: user(社長)=右の吹き出し / 相手(AI)・システム=左の吹き出し。
                return (
                  <Fragment key={i}>
                    {divider}
                    <div
                      style={{ ...S.msgRow, justifyContent: isUser ? 'flex-end' : 'flex-start' }}
                    >
                    {!isUser && (
                      <div style={{ ...S.avatar, background: av.bg }}>{av.ch}</div>
                    )}
                    <div style={{ maxWidth: '74%', display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
                      {!isUser && <div style={S.msgSender}>{sender}</div>}
                      <div
                        style={{
                          ...S.bubble,
                          ...(isUser ? S.bubbleUser : isAi ? S.bubbleAi : S.bubbleSystem),
                          ...('isWaiting' in m && m.isWaiting ? { opacity: 0.55 } : {}),
                        }}
                      >
                        {body}
                      </div>
                      {images}
                      {extras}
                      {tsLine}
                      {idLine}
                    </div>
                    </div>
                  </Fragment>
                );
              })}
            </div>

            {/* ↓ボタン: 会話がある間は常時表示。新着件数をバッジで示す。 */}
            {!!activeSession && shown.length > 0 && (
              <button
                style={S.jumpBtn}
                onClick={() => {
                  const el = logRef.current;
                  if (el) el.scrollTop = el.scrollHeight;
                  setShowJump(false);
                  setJumpCount(0);
                }}
              >
                {jumpCount > 0 && (
                  <span style={S.jumpBadge}>{jumpCount > 99 ? '99+' : jumpCount}</span>
                )}
                ↓
              </button>
            )}

            <div style={S.composerWrap}>
              {/* @// オートコンプリート。textarea の上にポップアップ表示。 */}
              {mention && (
                <div style={S.mentionPop}>
                  {mention.kind === '/' ? (
                    filteredCommands.length === 0 ? (
                      <div style={S.mentionHint}>該当する / コマンドがありません</div>
                    ) : (
                      filteredCommands.map((c, i) => {
                        const on = i === Math.min(mentionIndex, filteredCommands.length - 1);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            style={{ ...S.mentionItem, ...(on ? S.mentionItemOn : null) }}
                            onMouseEnter={() => setMentionIndex(i)}
                            onMouseDown={(e) => { e.preventDefault(); pickCommand(c); }}
                          >
                            <span style={S.mentionIcon}>{COMMAND_KIND_ICON[c.kind] ?? '⌨️'}</span>
                            <span style={S.mentionLabel}>/{c.name}</span>
                            {c.label && <span style={S.mentionSub}>{c.label}</span>}
                          </button>
                        );
                      })
                    )
                  ) : filteredMentions.length === 0 ? (
                    <div style={S.mentionHint}>
                      {activeTeamId == null ? 'チームを選ぶとメンバーに@できます' : '該当する相手がいません'}
                    </div>
                  ) : (
                    filteredMentions.map((c, i) => {
                      const on = i === Math.min(mentionIndex, filteredMentions.length - 1);
                      return (
                        <button
                          key={c.ref}
                          type="button"
                          style={{ ...S.mentionItem, ...(on ? S.mentionItemOn : null) }}
                          onMouseEnter={() => setMentionIndex(i)}
                          onMouseDown={(e) => { e.preventDefault(); pickMention(c); }}
                        >
                          <span style={S.mentionIcon}>{c.icon || (c.ref === 'controller' ? '⚙' : '🧑')}</span>
                          <span style={S.mentionLabel}>{c.label}</span>
                          {c.sub && <span style={S.mentionSub}>{c.sub}</span>}
                        </button>
                      );
                    })
                  )}
                  {/* コントローラ担当者の名前変更（ユーザー単位・全チャット共通）。 */}
                  {mention.kind === '@' && controller && (
                    <button
                      type="button"
                      style={S.mentionEdit}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const n = window.prompt('「コントローラ担当者」の表示名', controller.name);
                        if (n && n.trim()) renameController.mutate({ name: n.trim() });
                      }}
                    >
                      ✎ コントローラ担当者の名前を変更（現在: {controller.name}）
                    </button>
                  )}
                </div>
              )}
              <div style={S.composerBox}>
                {/* 上端ドラッグバー: つかんで上へ＝高く / 下へ＝低く。高さは localStorage 記憶。 */}
                <div
                  style={S.composerResizeHandle}
                  onMouseDown={startComposerResize}
                  title="上端をドラッグして入力欄の高さを変更"
                >
                  <div style={S.composerResizeGrip} />
                </div>
                {/* 添付画像のプレビュー（右上に赤✗でキャンセル・クリックで拡大ダイアログ）。 */}
                {pendingAtts.length > 0 && (
                  <div style={S.previewStrip}>
                    {pendingAtts.map((a) => (
                      <div key={a.id} style={S.previewItem}>
                        <img
                          src={a.url}
                          alt={a.name}
                          title={a.name}
                          style={S.previewImg}
                          onClick={() => setDialog({ url: a.url, name: a.name })}
                        />
                        <button
                          type="button"
                          style={S.previewRemove}
                          title="この画像を取り消す"
                          onClick={() => removeAtt(a.id)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <textarea
                  ref={setTaEl}
                  style={S.textarea}
                  placeholder={
                    activeTeamId === BROADCAST
                      ? globalBC
                        ? '全プロジェクトの全チームへ一斉発信（各チームに新スレッドが立ちます）'
                        : `#${active.name} 内の全チームへ一斉発信（各チームに新スレッドが立ちます）`
                      : `${pmName ?? personName} にメッセージ（@で宛先、#${active.name}）`
                  }
                  value={input}
                  onChange={onComposerChange}
                  onPaste={onPaste}
                  onKeyDown={(e) => {
                    // 候補ドロップダウン表示中は ↑↓ で選択移動・Enter/Tab で確定（送信とは別文脈）。
                    // @=宛先候補 / /=コマンド候補。kind により確定先(pickMention/pickCommand)を分ける。
                    const list =
                      mention?.kind === '@'
                        ? filteredMentions
                        : mention?.kind === '/'
                          ? filteredCommands
                          : [];
                    if (mention && list.length > 0) {
                      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex((i) => (i + 1) % list.length); return; }
                      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex((i) => (i - 1 + list.length) % list.length); return; }
                      if ((e.key === 'Enter' || e.key === 'Tab') && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        const idx = Math.min(mentionIndex, list.length - 1);
                        if (mention.kind === '/') pickCommand(filteredCommands[idx]);
                        else pickMention(filteredMentions[idx]);
                        return;
                      }
                    }
                    if (e.key === 'Escape' && mention) { setMention(null); return; }
                    // 送信は Ctrl+Enter（Mac は Cmd+Enter）のみ。Enter 単体は改行。
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      if (e.nativeEvent.isComposing) return; // IME 変換確定中は送信しない
                      e.preventDefault();
                      void send();
                    }
                  }}
                  rows={1}
                />
                <div style={S.composerBar}>
                  {/* 画像添付（ファイル選択）。スクショは textarea へ Ctrl+V でも貼れる。 */}
                  <button
                    type="button"
                    style={S.attachBtn}
                    title="画像を添付（ファイル選択／貼り付けも可）"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    📎
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files) addFiles(e.target.files);
                      e.target.value = ''; // 同じファイルを連続選択できるようにクリア
                    }}
                  />
                  {/* AI-WF262 (要望#211/TA-5): 📮非同期モード。ON=「受け取りました」→背面OK→完了push。 */}
                  <button
                    type="button"
                    data-testid="async-toggle"
                    style={{ ...S.asyncBtn, ...(asyncMode ? S.asyncBtnOn : null) }}
                    title={asyncMode
                      ? '非同期モードON: 送信すると「受け取りました」と受領され、画面を離れても完了時に通知が届きます（クリックでOFF）'
                      : '非同期モードOFF: クリックでON（用件を預けて離席できる留守番モード）'}
                    onClick={() => setAsyncMode((v) => !v)}
                  >
                    📮 非同期{asyncMode ? 'ON' : ''}
                  </button>
                  <span style={S.hint}>
                    {asyncMode
                      ? '📮 非同期: 受領後は画面を離れてOK・完了は通知でお知らせ / Ctrl+Enter送信'
                      : `@で宛先・/で機能 / Ctrl+Enter送信・Enterで改行${busy ? ' ・応答待ち（送信は続けられます）' : ''}`}
                  </span>
                  <button
                    style={{ ...S.sendBtn, ...(!input.trim() && pendingAtts.length === 0 ? S.sendBtnOff : null) }}
                    onClick={() => send()}
                    disabled={!input.trim() && pendingAtts.length === 0}
                  >
                    送信
                  </button>
                </div>
              </div>
            </div>
            {/* 画像拡大ダイアログ（背景クリック／×で閉じる）。 */}
            {dialog && (
              <div style={S.imgDialogBackdrop} onClick={() => setDialog(null)}>
                <div style={S.imgDialogInner} onClick={(e) => e.stopPropagation()}>
                  <div style={S.imgDialogBar}>
                    <span style={S.imgDialogName}>{dialog.name}</span>
                    <button type="button" style={S.imgDialogClose} onClick={() => setDialog(null)}>
                      ×
                    </button>
                  </div>
                  <img src={dialog.url} alt={dialog.name} style={S.imgDialogImg} />
                </div>
              </div>
            )}
          </>
        )}
      </section>
      </>
      )}
      {/* AI-WF265 (要望#213): 通話パネル＋着信モーダル（実体は src/call/ に隔離。ここは置くだけ）。 */}
      <CallDock />
    </div>
  );
}

const ABERGINE = '#3f0e40';
const S: Record<string, React.CSSProperties> = {
  wrap: { display: 'flex', height: 'calc(100vh - 53px)', width: '100%', background: '#fff', overflow: 'hidden' },
  // モバイルナビゲーション: ドリルダウン時の戻るバー・ボタン。
  mobileBackBar: { display: 'flex', alignItems: 'center', height: 44, padding: '0 12px', borderBottom: '1px solid #e7ebf0', background: '#f8f8fa', flexShrink: 0, gap: 8 },
  mobileBackBtn: { border: 'none', background: 'transparent', color: '#2563eb', fontSize: 20, fontWeight: 600, padding: '4px 8px 4px 0', cursor: 'pointer', flexShrink: 0, lineHeight: 1 },
  mobileBackTitle: { flex: 1, fontWeight: 700, fontSize: 15, color: '#1d1c1d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // メモ／タスクをチーム群から分ける水平線。
  notesSep: { height: 1, background: '#dfe3e8', margin: '8px 6px' },
  // AI-WF329(#259): touchAction:'none' でタッチ時のスクロール/ズームにハンドルのドラッグを奪われないようにする。
  resizer: { width: 11, marginLeft: -5, marginRight: -5, flexShrink: 0, cursor: 'col-resize', background: 'transparent', alignSelf: 'stretch', display: 'flex', justifyContent: 'center', zIndex: 5, touchAction: 'none' },
  resizerLine: { width: 2, alignSelf: 'stretch', background: 'transparent', transition: 'background 0.12s' },
  resizerLineActive: { background: '#1164a3' },
  side: { width: 230, flexShrink: 0, background: ABERGINE, color: '#cdbccd', display: 'flex', flexDirection: 'column' },
  workspace: { height: 50, display: 'flex', alignItems: 'center', padding: '0 16px', borderBottom: '1px solid rgba(255,255,255,0.12)', flexShrink: 0 },
  wsName: { color: '#fff', fontWeight: 700, fontSize: 15 },
  sideScroll: { flex: 1, overflowY: 'auto', padding: '10px 8px' },
  sectionHead: { fontSize: 12, color: '#b9a3b9', padding: '6px 10px', fontWeight: 600 },
  sectionHeadBtn: { display: 'flex', alignItems: 'center', gap: 4, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', color: '#b9a3b9', padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  typeGroupLabel: { fontSize: 10, fontWeight: 700, color: '#7a6a7a', textTransform: 'uppercase' as const, letterSpacing: '0.08em', padding: '8px 10px 2px 10px' },
  typeGroupLabelBtn: { display: 'flex', alignItems: 'center', gap: 4, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', fontSize: 10, fontWeight: 700, color: '#7a6a7a', textTransform: 'uppercase' as const, letterSpacing: '0.08em', padding: '8px 10px 2px 10px', cursor: 'pointer' },
  archiveHeadBtn: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: '4px 8px', cursor: 'pointer' },
  sideNote: { fontSize: 13, color: '#b9a3b9', padding: 10, lineHeight: 1.6 },
  chan: { display: 'flex', alignItems: 'center', gap: 9, width: '100%', border: 'none', background: 'transparent', color: '#cdbccd', padding: '7px 10px', borderRadius: 6, cursor: 'pointer', textAlign: 'left', marginBottom: 1 },
  chanActive: { background: '#1164a3', color: '#fff' },
  chanDragging: { opacity: 0.4 },
  chanArchived: { opacity: 0.72 },
  dragHandle: { color: '#8a738a', fontSize: 12, cursor: 'grab', flexShrink: 0, lineHeight: 1 },
  archiveZone: { marginTop: 12, paddingBottom: 8, minHeight: 64 },
  archiveHead: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px' },
  archiveLine: { flex: 1, height: 1, background: 'rgba(255,255,255,0.18)' },
  archiveLabel: { fontSize: 11, color: '#b9a3b9', fontWeight: 600, flexShrink: 0 },
  archiveHint: { fontSize: 11, color: '#8a738a', padding: '10px', textAlign: 'center', border: '1px dashed rgba(255,255,255,0.18)', borderRadius: 6, margin: '4px 6px' },
  chanDot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0 },
  chanText: { display: 'flex', flexDirection: 'column', lineHeight: 1.25, overflow: 'hidden' },
  chanName: { fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  chanWho: { fontSize: 11, opacity: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  teamCol: { width: 200, flexShrink: 0, borderRight: '1px solid #e7ebf0', background: '#fbfafc', display: 'flex', flexDirection: 'column' },
  teamEmpty: { fontSize: 12, color: '#9b9b9b', padding: '10px 12px', lineHeight: 1.6 },
  teamDot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0, display: 'inline-block' },
  threads: { width: 232, flexShrink: 0, borderRight: '1px solid #e7ebf0', background: '#f7f7f8', display: 'flex', flexDirection: 'column' },
  threadsEmpty: { margin: 'auto', color: '#94a3b8', fontSize: 13, padding: 16, textAlign: 'center' },
  threadsHead: { height: 50, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px', borderBottom: '1px solid #e7ebf0' },
  newBtn: { padding: '5px 10px', fontSize: 12, fontWeight: 700, background: '#fff', color: '#2563eb', border: '1px solid #cbd5e1', borderRadius: 7 },
  threadList: { flex: 1, overflowY: 'auto', padding: 8 },
  threadItem: { display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 2 },
  threadActive: { background: '#e8eefc' },
  threadActBtn: { flexShrink: 0, fontSize: 11, lineHeight: 1, padding: '2px 3px', borderRadius: 4, cursor: 'pointer', color: '#9b9b9b' },
  // チーム行の「VS Code で開く」リンク。トンネル名が登録済みの DIR があるときだけ出す。
  tunnelLink: { flexShrink: 0, fontSize: 12, lineHeight: 1, padding: '2px 3px', borderRadius: 4, cursor: 'pointer' },
  threadTitle: { fontSize: 13, fontWeight: 600, color: '#1d1c1d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'text' },
  threadSub: { fontSize: 11, color: '#9b9b9b', marginTop: 2 },
  titleInput: { width: '100%', fontSize: 13, fontWeight: 600, border: 'none', borderBottom: '1px solid #6366f1', outline: 'none', background: 'transparent', color: '#1d1c1d', padding: '0 0 1px 0' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: '#fff', position: 'relative' },
  jumpBtn: { position: 'absolute', right: 24, bottom: 80, width: 44, height: 44, borderRadius: '50%', background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.18)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: '#1d1c1d', zIndex: 10, padding: 0 },
  jumpBadge: { position: 'absolute', top: -5, right: -5, minWidth: 18, height: 18, borderRadius: 999, background: '#e01e5a', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1 },
  empty: { margin: 'auto', color: '#94a3b8' },
  head: { minHeight: 64, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 20px', borderBottom: '1px solid #e8e8e8' },
  // AI-WF329(#259): プロジェクト名(#名)は単一テキスト。狭幅で1文字ずつ折返す縦書き化を止めるため
  // nowrap+overflow+ellipsis 化(親は minWidth:0 済)。子アイコンは無いので display:flex を外しても PC 表示は不変。
  headTitle: { fontSize: 18, fontWeight: 800, color: '#1d1c1d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  headTeam: { fontSize: 13, fontWeight: 700, color: '#3730a3', background: '#eef2ff', borderRadius: 999, padding: '2px 10px' },
  headTopic: { fontSize: 13, color: '#616061', marginTop: 1 },
  // パネルヘッダのチームメンバー一覧（アバター＋名前。人が増えたら下へ折り返す）。
  headMembers: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 12, rowGap: 4, marginTop: 5 },
  member: { display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0 },
  memberAv: { width: 22, height: 22, borderRadius: '50%', background: '#eef2ff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 },
  // AI-WF329(#259): メンバー名の1文字折返し(縦書き化)を止める。nowrap+ellipsis、maxWidth は
  // 現実的な名前は全表示できる余裕値(狭いときだけ省略記号が出る)。
  memberName: { fontSize: 12.5, color: '#475569', fontWeight: 600, display: 'inline-block', maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' },
  memberPm: { fontSize: 11 },
  pcRow: { display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' },
  pcChip: { fontSize: 12, color: '#1d1c1d', background: '#f4f4f5', border: '1px solid #e3e3e4', borderRadius: 999, padding: '3px 10px', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' },
  pcDot: { width: 7, height: 7, borderRadius: '50%', display: 'inline-block', marginRight: 5 },
  log: { flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '16px 0', minHeight: 0, overscrollBehavior: 'contain' },
  logEmpty: { padding: '40px 28px' },
  logHash: { width: 52, height: 52, borderRadius: 14, background: ABERGINE, color: '#fff', fontSize: 26, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  msg: { display: 'flex', gap: 10, padding: '7px 20px', alignItems: 'flex-start' },
  // LINE風メッセージ行。user は右寄せ（justifyContent はインラインで指定）。
  msgRow: { display: 'flex', gap: 8, padding: '5px 20px', alignItems: 'flex-start', width: '100%' },
  avatar: { width: 36, height: 36, borderRadius: 8, flexShrink: 0, color: '#fff', fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  msgSender: { fontWeight: 700, color: '#6b6b6b', fontSize: 12, marginBottom: 2 },
  // 吹き出し（共通）。色は role 別で上書き。
  bubble: { borderRadius: 16, padding: '8px 13px', fontSize: 15, lineHeight: 1.55, wordBreak: 'break-word', boxShadow: '0 1px 1px rgba(0,0,0,0.06)' },
  bubbleUser: { background: '#06c755', color: '#fff', borderTopRightRadius: 4 }, // LINE グリーン（自分）
  bubbleAi: { background: '#ffffff', color: '#1d1c1d', border: '1px solid #e6e6e8', borderTopLeftRadius: 4 },
  bubbleSystem: { background: '#fff4f4', color: '#b91c1c', border: '1px solid #f3c4c4' },
  // Markdown 本文（吹き出し内）。先頭/末尾の段落マージンを詰める。
  mdBody: { fontSize: 15, lineHeight: 1.55 },
  // 表示モード切替トグル（ヘッダー右）。
  modeToggle: { marginLeft: 'auto', display: 'inline-flex', gap: 2, background: '#f1f1f3', borderRadius: 999, padding: 2, flexShrink: 0 },
  modeBtn: { border: 'none', background: 'transparent', color: '#6b6b6b', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, cursor: 'pointer' },
  modeBtnOn: { background: '#fff', color: '#1d1c1d', boxShadow: '0 1px 2px rgba(0,0,0,0.12)' },
  msgText: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55, fontSize: 15, color: '#1d1c1d', marginTop: 2 },
  msgSystem: { color: '#b91c1c' },
  // AI-WF251: 会話エリアの「考え中」クルクル（回転スピナー）。
  spinner: { width: 14, height: 14, borderRadius: '50%', border: '2px solid #cdeadd', borderTopColor: '#2bac76', display: 'inline-block', animation: 'koneccoSpin 0.7s linear infinite' },
  // AI-WF251 (C2): streaming 中の逐次表示インジケータ。
  typingInline: { display: 'inline-flex', alignItems: 'center', gap: 6, color: '#616061', fontSize: 13, fontStyle: 'italic' },
  streamCaret: { display: 'inline-block', marginLeft: 2, color: '#2bac76', fontWeight: 700, animation: 'koneccoBlink 1s step-end infinite' },
  // タスク#408→#413: run 識別子（sessionId= / requestId= の2行）・コピーアイコン・メッセージ単位の停止ボタン。
  runIdBlock: { display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4, maxWidth: '100%' },
  runIdRow: { display: 'flex', alignItems: 'center', gap: 6 },
  runIdText: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 10, color: '#9ca3af', wordBreak: 'break-all', userSelect: 'all' },
  copyBtn: { border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', borderRadius: 4, padding: '0 4px', fontSize: 11, lineHeight: '16px', cursor: 'pointer', flexShrink: 0 },
  msgStopBtn: { border: '1px solid #d92d20', background: '#fff', color: '#d92d20', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  // 会話状態バッジ（対応中/返信待ち）。
  statusBadge: { display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 700 },
  statusWorking: { background: '#eef2ff', color: '#2563eb', border: '1px solid #c7d2fe' },
  statusWaiting: { background: '#fff7ed', color: '#b45309', border: '1px solid #fed7aa' },
  // AI-WF262 (要望#211): 非同期の種別チップ（受領/作業中/完了）。
  kindAck: { background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' },
  kindProgress: { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' },
  kindFinal: { background: '#faf5ff', color: '#7e22ce', border: '1px solid #e9d5ff' },
  // 「動いてる」鼓動ログ（時刻つき・追記）。点滅ドット＋時刻付き作業内容の複数行。
  liveActivity: {
    display: 'inline-flex', alignItems: 'flex-start', gap: 7, marginTop: 6,
    padding: '5px 10px', borderRadius: 12, background: '#f0f9ff',
    border: '1px solid #bae6fd', color: '#0369a1', fontSize: 12.5, fontWeight: 600,
    lineHeight: 1.5,
  },
  liveDot: {
    width: 8, height: 8, borderRadius: '50%', background: '#0ea5e9',
    flexShrink: 0, marginTop: 5, animation: 'koneccoBlink 1s ease-in-out infinite',
  },
  liveTime: {
    fontVariantNumeric: 'tabular-nums', color: '#0891b2', opacity: 0.8,
    fontWeight: 700, marginRight: 2,
  },
  // メッセージ日時（送信/返信の時刻・秒精度）。
  msgTime: { fontSize: 11, color: '#94a3b8', marginTop: 3, fontVariantNumeric: 'tabular-nums' },
  // AI-WF262 (要望#211): ヘッダ右肩（🔔通知トグル＋表示モード切替）。
  headRight: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  // AI-WF262 (要望#211): 📮非同期モードトグル（コンポーザ左下・📎の隣）。
  asyncBtn: { border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#616061', padding: '3px 8px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  asyncBtnOn: { background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' },
  // コマンド/実行アクションの結果ブロック。
  actionList: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6, width: '100%' },
  actionItem: { background: '#f8f8f9', border: '1px solid #e6e6e8', borderRadius: 8, padding: '6px 10px' },
  actionHead: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 },
  actionStatusDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  actionName: { fontWeight: 700, color: '#1d1c1d' },
  actionStatusText: { fontSize: 12, fontWeight: 700, marginLeft: 'auto' },
  actionOutput: { margin: '6px 0 0', padding: '6px 8px', background: '#1d1c1d', color: '#e6e6e8', borderRadius: 6, fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 280, overflow: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  // AI-WF251: 本文の 表示/ソース タブ。
  bodyTabs: { display: 'flex', gap: 4, marginBottom: 6 },
  bodyTab: { fontSize: 11, lineHeight: 1, padding: '3px 8px', border: '1px solid #e0e0e2', borderRadius: 6, background: '#fff', color: '#8a8a8e', cursor: 'pointer', fontWeight: 700 },
  bodyTabActive: { background: '#eef1f6', color: '#1d1c1d', borderColor: '#c9ccd2' },
  bodySource: { margin: 0, padding: '8px 10px', background: '#f6f8fa', border: '1px solid #e6e6e8', borderRadius: 8, fontSize: 12.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#1d1c1d' },
  composerWrap: { flexShrink: 0, padding: '0 20px 20px', position: 'relative' },
  composerBox: { border: '1px solid #b6b6b8', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  mentionPop: { position: 'absolute', left: 20, right: 20, bottom: '100%', marginBottom: 6, background: '#fff', border: '1px solid #d7d7d9', borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.14)', padding: 4, maxHeight: 240, overflowY: 'auto', zIndex: 20 },
  mentionItem: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', border: 'none', background: 'transparent', padding: '7px 10px', borderRadius: 7, cursor: 'pointer', textAlign: 'left' },
  mentionIcon: { width: 22, height: 22, borderRadius: 6, background: '#f0eef2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 },
  mentionLabel: { fontSize: 14, fontWeight: 600, color: '#1d1c1d', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  mentionSub: { fontSize: 11, color: '#8a738a', flexShrink: 0 },
  mentionHint: { fontSize: 12, color: '#9b9b9b', padding: '10px 12px' },
  mentionEdit: { display: 'block', width: '100%', textAlign: 'left', border: 'none', borderTop: '1px solid #eee', background: 'transparent', color: '#616061', fontSize: 12, padding: '8px 10px', marginTop: 2, cursor: 'pointer' },
  // 高さは縦方向ドラッグでリサイズ可（ResizeObserver で localStorage に記憶）。
  textarea: { width: '100%', border: 'none', resize: 'none', padding: '12px 14px', fontSize: 15, fontFamily: 'inherit', lineHeight: 1.5, outline: 'none', display: 'block', minHeight: 80, maxHeight: '70vh' },
  // 入力欄の上端ドラッグバー（高さ変更）。ホバーで掴めると分かるグリップを中央に出す。
  composerResizeHandle: { height: 12, cursor: 'ns-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f4f5', borderBottom: '1px solid #ededef' },
  composerResizeGrip: { width: 36, height: 3, borderRadius: 999, background: '#c4c4c8' },
  // 「ここから未読」区切り（中央ラベル＋両側の線）。
  unreadDivider: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 20px', margin: '4px 0' },
  unreadDividerLine: { flex: 1, height: 1, background: '#f6c6d5' },
  unreadDividerLabel: { fontSize: 11, fontWeight: 700, color: '#e01e5a', background: '#fdecf1', border: '1px solid #f6c6d5', borderRadius: 999, padding: '2px 12px', whiteSpace: 'nowrap', flexShrink: 0 },
  mentionItemOn: { background: '#eef2ff' },
  composerBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px 8px' },
  hint: { fontSize: 12, color: '#9b9b9b' },
  sendBtn: { padding: '7px 18px', borderRadius: 8, border: 'none', background: '#007a5a', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  sendBtnOff: { background: '#e8e8e8', color: '#a0a0a0', cursor: 'not-allowed' },
  // 添付画像（チャット欄表示）。複数枚は折り返して並べる。
  imgRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  chatImg: { maxWidth: 220, maxHeight: 220, borderRadius: 10, border: '1px solid #e6e6e8', cursor: 'pointer', objectFit: 'cover', display: 'block' },
  imgPlaceholder: { width: 120, height: 90, borderRadius: 10, background: '#f1f1f3', border: '1px solid #e6e6e8' },
  // 送信前プレビュー（composer 内・右上に赤✗）。
  previewStrip: { display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 12px 2px' },
  previewItem: { position: 'relative', width: 72, height: 72 },
  previewImg: { width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e6e6e8', cursor: 'pointer', display: 'block' },
  previewRemove: { position: 'absolute', top: -7, right: -7, width: 20, height: 20, borderRadius: '50%', border: '2px solid #fff', background: '#f43f5e', color: '#fff', fontSize: 13, fontWeight: 700, lineHeight: '16px', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' },
  attachBtn: { border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', padding: '2px 6px', borderRadius: 6, lineHeight: 1, marginRight: 2 },
  // 画像拡大ダイアログ。
  imgDialogBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 },
  imgDialogInner: { maxWidth: '92vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: '#1d1c1d', borderRadius: 12, overflow: 'hidden', boxShadow: '0 12px 48px rgba(0,0,0,0.5)' },
  imgDialogBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 12px', color: '#fff' },
  imgDialogName: { fontSize: 13, color: '#e5e5e5', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  imgDialogClose: { border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', width: 28, height: 28, borderRadius: '50%', fontSize: 18, lineHeight: '24px', cursor: 'pointer', flexShrink: 0 },
  imgDialogImg: { maxWidth: '92vw', maxHeight: 'calc(92vh - 44px)', objectFit: 'contain', display: 'block' },
};
